import React, { useEffect, useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './App.css';
import LoginPage, { isLoggedIn, doLogout, checkPasswordVersion } from './LoginPage';

// ─── Crop helpers ─────────────────────────────────────────────────
/**
 * Produce a cropped Blob from an image URL + pixelCrop area.
 * The output is upscaled to `outputWidth` × `outputHeight` (or proportional
 * if only one is given) so the final image is always high resolution.
 */
async function getCroppedBlob(
  imageSrc,
  pixelCrop,
  { outputType = 'image/jpeg', quality = 0.92, outputWidth = null, outputHeight = null } = {}
) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // No crossOrigin for blob: URLs — it breaks them
    img.onload = () => {
      // Determine output canvas size
      let canvasW, canvasH;
      if (outputWidth && outputHeight) {
        canvasW = outputWidth;
        canvasH = outputHeight;
      } else if (outputWidth) {
        canvasW = outputWidth;
        canvasH = Math.round(outputWidth * (pixelCrop.height / pixelCrop.width));
      } else if (outputHeight) {
        canvasH = outputHeight;
        canvasW = Math.round(outputHeight * (pixelCrop.width / pixelCrop.height));
      } else {
        // Default: use natural crop size but cap at 1920 wide
        const scale = Math.min(1, 1920 / pixelCrop.width);
        canvasW = Math.round(pixelCrop.width * scale);
        canvasH = Math.round(pixelCrop.height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        img,
        pixelCrop.x, pixelCrop.y,
        pixelCrop.width, pixelCrop.height,
        0, 0,
        canvasW, canvasH
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
          resolve(blob);
        },
        outputType,
        quality
      );
    };
    img.onerror = (err) => reject(err);
    img.src = imageSrc;
  });
}

/**
 * ImageCropModal — reusable crop/zoom/pan modal.
 *
 * Props:
 *   imageSrc     {string}  — object URL of image to crop
 *   aspect       {number}  — width/height ratio (e.g. 16/9). Pass null for free crop.
 *   onCancel     {fn}
 *   onConfirm    {fn(croppedBlob, croppedPreviewUrl)}
 *   title        {string}  — modal heading
 *   extraActions {node}    — extra buttons rendered between Cancel and Crop & Use
 *   outputWidth  {number}  — desired output canvas width in pixels (optional)
 *   outputHeight {number}  — desired output canvas height in pixels (optional)
 */
function ImageCropModal({
  imageSrc,
  aspect,
  onCancel,
  onConfirm,
  title = 'Crop Image',
  extraActions,
  outputWidth = null,
  outputHeight = null,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, cap) => setCroppedAreaPixels(cap), []);

  // When the media loads, compute a sensible minZoom so the image always fills the crop box
  const onMediaLoaded = useCallback((mediaSize) => {
    // react-easy-crop passes { width, height, naturalWidth, naturalHeight }
    // minZoom = 1 already ensures the image fills the crop box width;
    // for very tall images with a wide aspect ratio we may need to push it higher.
    // Let react-easy-crop handle it — just ensure minZoom ≤ 1.
    setMinZoom(1);
    setZoom(1);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, {
        outputWidth,
        outputHeight,
      });
      const previewUrl = URL.createObjectURL(blob);
      onConfirm(blob, previewUrl);
    } catch (e) {
      alert('Crop failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="crop-overlay">
      <div className="crop-modal">
        <div className="crop-modal-header">
          <span className="crop-modal-title">{title}</span>
          <button className="crop-close-btn" onClick={onCancel} disabled={busy}>✕</button>
        </div>
        <div className="crop-area">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={5}
            aspect={aspect || undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onMediaLoaded={onMediaLoaded}
            objectFit="auto-cover"
            showGrid={true}
            style={{
              containerStyle: { background: '#222' },
              mediaStyle: { maxHeight: '100%' },
            }}
          />
        </div>
        <div className="crop-controls">
          <div className="crop-hint">Drag to reposition · Scroll or use slider to zoom</div>
          <label className="crop-zoom-label">
            Zoom
            <input
              type="range"
              min={minZoom}
              max={5}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="crop-zoom-slider"
            />
          </label>
          <div className="crop-actions">
            <button className="crop-btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
            {extraActions}
            <button className="crop-btn-confirm" onClick={handleConfirm} disabled={busy}>
              {busy ? 'Processing…' : 'Crop & Use'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const API = process.env.REACT_APP_API_URL;

// ─── Logo restrictions ────────────────────────────────────────────
const LOGO_MAX_SIZE_MB = 2;
const LOGO_MAX_SIZE_BYTES = LOGO_MAX_SIZE_MB * 1024 * 1024;
const LOGO_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
const LOGO_ALLOWED_LABEL = 'PNG, JPG, SVG, WEBP';
const REC_WIDTH = 200;
const REC_HEIGHT = 60;

// ─── Gallery restrictions ─────────────────────────────────────────
const GALLERY_MAX_SIZE_MB = 100;
const GALLERY_MAX_SIZE_BYTES = GALLERY_MAX_SIZE_MB * 1024 * 1024;
const GALLERY_ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const GALLERY_ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
const GALLERY_ALLOWED_TYPES = [...GALLERY_ALLOWED_IMAGE_TYPES, ...GALLERY_ALLOWED_VIDEO_TYPES];
const GALLERY_ALLOWED_LABEL = 'JPG, PNG, WEBP, GIF, MP4, WEBM, MOV, AVI';
const THUMB_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
}

// ═══════════════════════════════════════════════════════════════════
// ─── Logo Management Section ──────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function LogoSection() {
  const [currentLogo, setCurrentLogo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  // Prevent browser from opening dropped files in a new tab (page-level)
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  const fetchLogo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/logo`);
      const data = await res.json();
      setCurrentLogo(data.logoUrl || null);
    } catch {
      setCurrentLogo(null);
    }
  }, []);

  useEffect(() => { fetchLogo(); }, [fetchLogo]);

  const validateAndSetLogo = (file) => {
    if (!file) return;
    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      setError(`Invalid file type. Only ${LOGO_ALLOWED_LABEL} are allowed.`);
      setSelectedFile(null); setPreview(null); return;
    }
    if (file.size > LOGO_MAX_SIZE_BYTES) {
      setError(`File too large. Max ${LOGO_MAX_SIZE_MB}MB (yours: ${(file.size / 1024 / 1024).toFixed(2)}MB).`);
      setSelectedFile(null); setPreview(null); return;
    }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleFileChange = (e) => {
    setError(''); setSuccess('');
    validateAndSetLogo(e.target.files[0]);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    const file = e.dataTransfer.files[0];
    validateAndSetLogo(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setLoading(true); setError(''); setSuccess('');
    const formData = new FormData();
    formData.append('logo', selectedFile);
    try {
      const res = await fetch(`${API}/api/logo/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setSuccess('Logo uploaded! It is now live on the website.');
        setSelectedFile(null); setPreview(null);
        fileInputRef.current.value = '';
        fetchLogo();
      } else {
        setError(data.message || 'Upload failed.');
      }
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the logo?')) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API}/api/logo`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { setSuccess('Logo removed.'); setCurrentLogo(null); }
      else setError(data.message);
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="card restrictions-card">
        <h2>Upload Requirements</h2>
        <div className="restrictions-grid">
          <div className="restriction-item"><span className="restriction-label">Max File Size</span><span className="restriction-value">{LOGO_MAX_SIZE_MB} MB</span></div>
          <div className="restriction-item"><span className="restriction-label">Allowed Formats</span><span className="restriction-value">{LOGO_ALLOWED_LABEL}</span></div>
          <div className="restriction-item"><span className="restriction-label">Recommended Size</span><span className="restriction-value">{REC_WIDTH} × {REC_HEIGHT} px</span></div>
          <div className="restriction-item"><span className="restriction-label">Display Location</span><span className="restriction-value">Top-right of Navbar</span></div>
        </div>
      </div>

      <div className="card">
        <h2>Current Logo</h2>
        {currentLogo ? (
          <div className="current-logo-box">
            <div className="logo-preview-nav">
              <span className="nav-preview-label">Preview (as shown in navbar)</span>
              <div className="fake-navbar">
                <span className="fake-nav-brand">Elite Borewells</span>
                <img src={currentLogo} alt="Current Logo" className="navbar-logo-preview" />
              </div>
            </div>
            <button className="btn btn-danger" onClick={handleRemove} disabled={loading}>Remove Logo</button>
          </div>
        ) : (
          <p className="no-logo-text">No logo uploaded yet.</p>
        )}
      </div>

      <div className="card">
        <h2>Upload New Logo</h2>
        <div
          className="upload-area"
          onClick={() => fileInputRef.current.click()}
          onDrop={handleLogoDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {preview ? (
            <img src={preview} alt="Preview" className="upload-preview-img" />
          ) : (
            <>
              <div className="upload-icon">📁</div>
              <p className="upload-hint">Click to browse or drag & drop</p>
              <p className="upload-sub">{LOGO_ALLOWED_LABEL} — max {LOGO_MAX_SIZE_MB}MB — recommended {REC_WIDTH}×{REC_HEIGHT}px</p>
            </>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden-input" onChange={handleFileChange} />
        {selectedFile && (
          <div className="file-info">
            <span>📄 {selectedFile.name}</span>
            <span>{formatBytes(selectedFile.size)}</span>
            <span>{selectedFile.type}</span>
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <div className="upload-actions">
          <button className="btn btn-primary" onClick={handleUpload} disabled={!selectedFile || loading}>
            {loading ? 'Uploading...' : 'Upload Logo'}
          </button>
          {selectedFile && (
            <button className="btn btn-secondary" onClick={() => { setSelectedFile(null); setPreview(null); setError(''); fileInputRef.current.value = ''; }}>Cancel</button>
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Queue Item Component ─────────────────────────────────────────
// Each file in the upload queue gets its own card with caption +
// thumbnail picker (videos only). Status: idle | uploading | done | error
// ═══════════════════════════════════════════════════════════════════
function QueueItem({ item, onRemove, onCaptionChange, onThumbChange, onThumbRemove }) {
  const thumbInputRef = useRef();
  const isVideo = item.isVideo;

  const handleThumbPick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!THUMB_ALLOWED_TYPES.includes(file.type)) {
      alert('Thumbnail must be JPG, PNG or WEBP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Thumbnail too large. Max 5MB.');
      return;
    }
    onThumbChange(item.id, file, URL.createObjectURL(file));
    e.target.value = '';
  };

  const statusColor = item.status === 'done' ? '#276749' : item.status === 'error' ? '#c53030' : '#2b4c7e';

  return (
    <div className={`queue-item ${item.status}`}>
      {/* Preview */}
      <div className="queue-preview-wrap">
        {isVideo ? (
          <video src={item.previewUrl} className="queue-preview-media" muted playsInline />
        ) : (
          <img src={item.previewUrl} alt="preview" className="queue-preview-media" />
        )}
        <div className="queue-type-chip" style={{ background: isVideo ? '#b7791f' : '#2b4c7e' }}>
          {isVideo ? '🎬 VIDEO' : '📷 PHOTO'}
        </div>
        {item.status === 'idle' && (
          <button className="queue-remove-btn" onClick={() => onRemove(item.id)} title="Remove from queue">✕</button>
        )}
        {item.status === 'uploading' && (
          <div className="queue-uploading-overlay"><div className="spinner-sm" /></div>
        )}
        {item.status === 'done' && (
          <div className="queue-done-overlay">✓</div>
        )}
        {item.status === 'error' && (
          <div className="queue-error-overlay">✕</div>
        )}
      </div>

      {/* Details */}
      <div className="queue-details">
        <p className="queue-filename" title={item.file.name}>{item.file.name}</p>
        <p className="queue-filesize">{formatBytes(item.file.size)}</p>

        {/* Caption */}
        {item.status === 'idle' && (
          <input
            type="text"
            className="caption-input"
            placeholder="Add a caption (optional)"
            value={item.caption}
            onChange={(e) => onCaptionChange(item.id, e.target.value)}
            maxLength={120}
          />
        )}
        {item.status !== 'idle' && item.caption && (
          <p className="queue-caption-done">{item.caption}</p>
        )}

        {/* Thumbnail — only for videos, only when idle */}
        {isVideo && item.status === 'idle' && (
          <div className="queue-thumb-box">
            <div className="queue-thumb-label">
              🖼️ Thumbnail <span className="thumb-optional">(optional)</span>
            </div>
            {item.thumbPreview ? (
              <div className="queue-thumb-preview-row">
                <img src={item.thumbPreview} alt="thumb" className="queue-thumb-img" />
                <div className="queue-thumb-actions">
                  <button className="btn btn-xs btn-secondary" onClick={() => thumbInputRef.current.click()}>Change</button>
                  <button className="btn btn-xs btn-danger" onClick={() => onThumbRemove(item.id)}>Remove</button>
                </div>
              </div>
            ) : (
              <button className="btn btn-xs btn-secondary queue-thumb-pick-btn" onClick={() => thumbInputRef.current.click()}>
                + Select Thumbnail
              </button>
            )}
            <input
              ref={thumbInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden-input"
              onChange={handleThumbPick}
            />
          </div>
        )}

        {/* Status message */}
        {item.status === 'done' && <p className="queue-status-msg" style={{ color: statusColor }}>Uploaded successfully</p>}
        {item.status === 'error' && <p className="queue-status-msg" style={{ color: statusColor }}>{item.errorMsg || 'Upload failed'}</p>}
        {item.status === 'uploading' && <p className="queue-status-msg" style={{ color: statusColor }}>Uploading...</p>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Change Thumbnail Modal ───────────────────────────────────────
// Opens when user clicks "Change Thumbnail" on an existing video card
// ═══════════════════════════════════════════════════════════════════
function ChangeThumbnailModal({ item, onClose, onChanged }) {
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(item.thumbnail ? `${API}/gallery/${item.thumbnail}` : null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handlePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!THUMB_ALLOWED_TYPES.includes(file.type)) { setError('Must be JPG, PNG or WEBP.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Max 5MB.'); return; }
    setError('');
    setThumbFile(file);
    setThumbPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!thumbFile) return;
    setUploading(true); setError('');
    const formData = new FormData();
    formData.append('thumbnail', thumbFile);
    try {
      const res = await fetch(`${API}/api/gallery/${item.filename}/thumbnail`, { method: 'PATCH', body: formData });
      const data = await res.json();
      if (data.success) {
        onChanged(item.filename, data.thumbnail);
        onClose();
      } else {
        setError(data.message || 'Failed to update thumbnail.');
      }
    } catch { setError('Could not connect to server.'); }
    finally { setUploading(false); }
  };

  const handleRemoveThumbnail = async () => {
    if (!window.confirm('Remove the thumbnail for this video?')) return;
    setUploading(true); setError('');
    try {
      const res = await fetch(`${API}/api/gallery/${item.filename}/thumbnail`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        onChanged(item.filename, null);
        onClose();
      } else {
        setError(data.message || 'Failed to remove thumbnail.');
      }
    } catch { setError('Could not connect to server.'); }
    finally { setUploading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Change Video Thumbnail</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <p className="modal-sub">JPG, PNG or WEBP — max 5MB</p>

        <div
          className="modal-thumb-drop"
          onClick={() => inputRef.current.click()}
        >
          {thumbPreview ? (
            <img src={thumbPreview} alt="thumbnail" className="modal-thumb-img" />
          ) : (
            <div className="modal-thumb-empty">
              <span>🖼️</span>
              <p>Click to select a thumbnail</p>
            </div>
          )}
        </div>

        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden-input" onChange={handlePick} />

        {error && <div className="alert alert-error">{error}</div>}

        <div className="modal-actions">
          <button className="btn btn-primary" onClick={handleSave} disabled={!thumbFile || uploading}>
            {uploading ? 'Saving...' : 'Save Thumbnail'}
          </button>
          {item.thumbnail && (
            <button className="btn btn-danger" onClick={handleRemoveThumbnail} disabled={uploading}>
              Remove Thumbnail
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Banner Management Section ────────────────────────────────────
// Manages the hero/banner image shown on the homepage
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// ─── Home Services Cards Section ──────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function HomeServicesSection() {
  const [services, setServices] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null); // which card is open for text editing
  const [editForm, setEditForm] = useState({ label: '', icon: '', desc: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' }); // {type:'success'|'error', text}

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 3500);
  };

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/home-services`);
      const data = await res.json();
      if (data.success) setServices(data.services || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const openEdit = (svc) => {
    setEditingSlug(svc.slug);
    setEditForm({ label: svc.label, icon: svc.icon, desc: svc.desc });
  };

  const cancelEdit = () => { setEditingSlug(null); };

  const saveEdit = async (slug) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/home-services/${slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (data.success) {
        setServices((prev) => prev.map((s) => s.slug === slug ? { ...s, ...editForm } : s));
        setEditingSlug(null);
        showMsg('success', 'Service card saved!');
      } else {
        showMsg('error', data.message || 'Save failed.');
      }
    } catch { showMsg('error', 'Could not connect to server.'); }
    finally { setSaving(false); }
  };


  return (
    <div className="hs-section">
      {msg.text && (
        <div className={`hs-msg hs-msg-${msg.type}`}>{msg.text}</div>
      )}
      <div className="hs-grid">
        {services.map((svc) => (
          <div className="hs-card" key={svc.slug}>
            {/* Card icon area */}
            <div className="hs-card-img-area">
              <div className="hs-card-no-img">
                <span className="hs-card-icon">{svc.icon}</span>
              </div>
            </div>

            {/* Text info or edit form */}
            <div className="hs-card-body">
              {editingSlug === svc.slug ? (
                <div className="hs-edit-form">
                  <label className="hs-field-label">Icon (emoji)</label>
                  <input
                    className="hs-input"
                    value={editForm.icon}
                    onChange={(e) => setEditForm((f) => ({ ...f, icon: e.target.value }))}
                    maxLength={10}
                    placeholder="e.g. 🔩"
                  />
                  <label className="hs-field-label">Title</label>
                  <input
                    className="hs-input"
                    value={editForm.label}
                    onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
                    maxLength={120}
                    placeholder="Service name"
                  />
                  <label className="hs-field-label">Description</label>
                  <textarea
                    className="hs-textarea"
                    value={editForm.desc}
                    onChange={(e) => setEditForm((f) => ({ ...f, desc: e.target.value }))}
                    maxLength={400}
                    rows={3}
                    placeholder="Short description shown on the home page card"
                  />
                  <div className="hs-edit-actions">
                    <button className="hs-btn-save" onClick={() => saveEdit(svc.slug)} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="hs-btn-cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="hs-card-label">{svc.label}</div>
                  <div className="hs-card-desc">{svc.desc}</div>
                  <button className="hs-btn-edit" onClick={() => openEdit(svc)}>Edit Text</button>
                </>
              )}
            </div>

          </div>
        ))}
      </div>
    </div>
  );
}

function BannerSection() {
  const [banners, setBanners] = useState([]); // [{filename, url}, ...]
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const fileInputRef = useRef();
  // Crop modal state
  const [cropSrc, setCropSrc] = useState(null); // raw object URL for cropper

  const BANNER_MAX_MB = 10;
  const BANNER_MAX_BYTES = BANNER_MAX_MB * 1024 * 1024;
  const BANNER_ALLOWED = ['image/png', 'image/jpeg', 'image/webp'];
  const BANNER_ALLOWED_LABEL = 'PNG, JPG, WEBP';
  const BANNER_MAX_COUNT = 5;

  const fetchBanners = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/banners`);
      const data = await res.json();
      setBanners(data.banners || []);
    } catch {
      setBanners([]);
    }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const validateAndOpenCrop = (file) => {
    if (!file) return;
    if (!BANNER_ALLOWED.includes(file.type)) {
      setError(`Invalid file type. Only ${BANNER_ALLOWED_LABEL} are allowed.`);
      return;
    }
    if (file.size > BANNER_MAX_BYTES) {
      setError(`File too large. Max ${BANNER_MAX_MB}MB (yours: ${(file.size / 1024 / 1024).toFixed(2)}MB).`);
      return;
    }
    setError('');
    // Open crop modal with the raw file
    setCropSrc(URL.createObjectURL(file));
  };

  const handleFileChange = (e) => {
    setError(''); setSuccess('');
    validateAndOpenCrop(e.target.files[0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isFull) return;
    setError(''); setSuccess('');
    validateAndOpenCrop(e.dataTransfer.files[0]);
  };

  const handleCropConfirm = (blob, previewUrl) => {
    // Convert blob to File so FormData works correctly
    const file = new File([blob], 'banner.jpg', { type: 'image/jpeg' });
    setSelectedFile(file);
    setPreview(previewUrl);
    setCropSrc(null);
  };

  const handleCropCancel = () => {
    setCropSrc(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    if (banners.length >= BANNER_MAX_COUNT) {
      setError(`Maximum ${BANNER_MAX_COUNT} banners already uploaded. Delete one first.`);
      return;
    }
    setLoading(true); setError(''); setSuccess('');
    const formData = new FormData();
    formData.append('banner', selectedFile);
    try {
      const res = await fetch(`${API}/api/banners/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setSuccess(`Banner ${banners.length + 1} uploaded! Slideshow updated on homepage.`);
        setSelectedFile(null); setPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setBanners(data.banners || []);
      } else {
        setError(data.message || 'Upload failed.');
      }
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  const handleRemove = async (filename) => {
    setDeletingFile(filename);
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API}/api/banners/${encodeURIComponent(filename)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setSuccess('Banner removed.');
        setBanners(data.banners || []);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message);
      }
    } catch { setError('Could not connect to server.'); }
    finally { setDeletingFile(null); }
  };

  const isFull = banners.length >= BANNER_MAX_COUNT;

  return (
    <>
      <div className="card restrictions-card">
        <h2>Upload Requirements</h2>
        <div className="restrictions-grid">
          <div className="restriction-item"><span className="restriction-label">Max File Size</span><span className="restriction-value">{BANNER_MAX_MB} MB</span></div>
          <div className="restriction-item"><span className="restriction-label">Allowed Formats</span><span className="restriction-value">{BANNER_ALLOWED_LABEL}</span></div>
          <div className="restriction-item"><span className="restriction-label">Recommended Size</span><span className="restriction-value">1920 × 800 px</span></div>
          <div className="restriction-item"><span className="restriction-label">Max Banners</span><span className="restriction-value">{BANNER_MAX_COUNT} images</span></div>
        </div>
      </div>

      <div className="card">
        <div className="banner-list-header">
          <h2>Current Banners</h2>
          <span className={`banner-count-badge ${isFull ? 'banner-count-full' : ''}`}>
            {banners.length} / {BANNER_MAX_COUNT}
          </span>
        </div>
        {banners.length === 0 ? (
          <p className="no-logo-text">No banners uploaded yet. The homepage shows the default gradient background.</p>
        ) : (
          <div className="banner-thumbs-grid">
            {banners.map((b, i) => (
              <div className="banner-thumb-card" key={b.filename}>
                <div className="banner-thumb-index">Slide {i + 1}</div>
                <img src={b.url} alt={`Banner ${i + 1}`} className="banner-thumb-img" />
                <button
                  className="banner-thumb-delete"
                  onClick={() => handleRemove(b.filename)}
                  disabled={deletingFile === b.filename}
                  title="Remove this banner"
                >
                  {deletingFile === b.filename ? '...' : '✕'}
                </button>
              </div>
            ))}
          </div>
        )}
        {success && <div className="alert alert-success" style={{ marginTop: 12 }}>{success}</div>}
        {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}
      </div>

      <div className="card">
        <h2>Upload New Banner {isFull && <span className="banner-full-tag">Max reached — delete one first</span>}</h2>
        <div
          className={`upload-area ${isFull ? 'upload-area-disabled' : ''}`}
          style={{ minHeight: 180 }}
          onClick={() => !isFull && fileInputRef.current.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {preview ? (
            <img src={preview} alt="Preview" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 8 }} />
          ) : (
            <>
              <div className="upload-icon">{isFull ? '🚫' : '🖼️'}</div>
              <p className="upload-hint">{isFull ? 'Maximum 5 banners reached' : 'Click to browse or drag & drop'}</p>
              <p className="upload-sub">{isFull ? 'Delete a banner above to upload a new one' : `${BANNER_ALLOWED_LABEL} — max ${BANNER_MAX_MB}MB — recommended 1920×800px`}</p>
            </>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden-input" onChange={handleFileChange} disabled={isFull} />
        {selectedFile && (
          <div className="file-info">
            <span>📄 {selectedFile.name}</span>
            <span>{formatBytes(selectedFile.size)}</span>
            <span>{selectedFile.type}</span>
          </div>
        )}
        <div className="upload-actions">
          <button className="btn btn-primary" onClick={handleUpload} disabled={!selectedFile || loading || isFull}>
            {loading ? 'Uploading...' : 'Upload Banner'}
          </button>
          {selectedFile && (
            <button className="btn btn-secondary" onClick={() => { setSelectedFile(null); setPreview(null); setError(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Crop modal — shown when a raw file is picked */}
      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          aspect={16 / 9}
          title="Crop Banner (16 : 9)"
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
          outputWidth={1920}
          outputHeight={1080}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Gallery Management Section ───────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function GallerySection() {
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [queue, setQueue] = useState([]); // array of queue item objects
  const [uploading, setUploading] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [globalSuccess, setGlobalSuccess] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingCaption, setEditingCaption] = useState(null);
  const [editCaptionValue, setEditCaptionValue] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed
  const [thumbModal, setThumbModal] = useState(null); // item to change thumbnail for
  const fileInputRef = useRef();
  let nextId = useRef(Date.now());
  // Crop queue: images waiting to be cropped one by one
  const [cropQueue, setCropQueue] = useState([]); // [{file, previewUrl}, ...]
  // cropQueue[0] is the one currently in the crop modal

  // Gallery banner state
  const [bannerUrl, setBannerUrl] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerDeleting, setBannerDeleting] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [bannerSuccess, setBannerSuccess] = useState('');
  const [bannerCropSrc, setBannerCropSrc] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [bannerCropFile, setBannerCropFile] = useState(null);
  const bannerInputRef = useRef();

  const fetchItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const res = await fetch(`${API}/api/gallery`);
      const data = await res.json();
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoadingItems(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ── Gallery Banner ──────────────────────────────────────────────
  const fetchBanner = useCallback(async () => {
    setBannerLoading(true);
    try {
      const res = await fetch(`${API}/api/gallery-banner`);
      const data = await res.json();
      setBannerUrl(data.bannerUrl || null);
    } catch { setBannerUrl(null); }
    finally { setBannerLoading(false); }
  }, []);

  useEffect(() => { fetchBanner(); }, [fetchBanner]);

  const handleBannerFilePick = (e) => {
    setBannerError(''); setBannerSuccess('');
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setBannerError('Only JPG, PNG or WEBP images allowed.');
      return;
    }
    const url = URL.createObjectURL(file);
    setBannerCropFile(file);
    setBannerCropSrc(url);
  };

  const handleBannerCropConfirm = async (blob) => {
    setBannerCropSrc(null);
    setBannerCropFile(null);
    setBannerError(''); setBannerSuccess('');
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append('banner', blob, 'gallery-banner.jpg');
      const res = await fetch(`${API}/api/gallery-banner/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        setBannerUrl(data.bannerUrl);
        setBannerSuccess('Gallery banner updated!');
      } else {
        setBannerError(data.message || 'Upload failed.');
      }
    } catch { setBannerError('Upload failed.'); }
    finally { setBannerUploading(false); }
  };

  const handleBannerCropCancel = () => {
    if (bannerCropSrc) URL.revokeObjectURL(bannerCropSrc);
    setBannerCropSrc(null);
    setBannerCropFile(null);
  };

  const handleBannerDelete = async () => {
    if (!window.confirm('Remove the gallery page banner?')) return;
    setBannerError(''); setBannerSuccess('');
    setBannerDeleting(true);
    try {
      const res = await fetch(`${API}/api/gallery-banner`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { setBannerUrl(null); setBannerSuccess('Banner removed.'); }
      else { setBannerError(data.message || 'Delete failed.'); }
    } catch { setBannerError('Delete failed.'); }
    finally { setBannerDeleting(false); }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const h = (e) => {
      if (thumbModal) { if (e.key === 'Escape') setThumbModal(null); return; }
      if (lightboxIndex === null) return;
      const visibleItems = items.filter((i) => filter === 'all' || i.type === filter);
      const len = visibleItems.length;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i > 0 ? i - 1 : len - 1));
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i < len - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightboxIndex, thumbModal, items, filter]);

  // ── File picker handler — supports multiple files ──────────────
  const handleFilesPicked = (e) => {
    setGlobalError(''); setGlobalSuccess('');
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const errors = [];
    const imagesToCrop = [];
    const newItems = [];

    files.forEach((file) => {
      if (!GALLERY_ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" — invalid type (allowed: ${GALLERY_ALLOWED_LABEL})`);
        return;
      }
      if (file.size > GALLERY_MAX_SIZE_BYTES) {
        errors.push(`"${file.name}" — too large (max ${GALLERY_MAX_SIZE_MB}MB)`);
        return;
      }
      const isVideo = GALLERY_ALLOWED_VIDEO_TYPES.includes(file.type);
      if (isVideo) {
        // Videos go straight to queue (no crop)
        newItems.push({
          id: nextId.current++,
          file,
          isVideo: true,
          previewUrl: URL.createObjectURL(file),
          caption: '',
          thumbFile: null,
          thumbPreview: null,
          status: 'idle',
          errorMsg: '',
        });
      } else {
        // Images go through crop modal first
        imagesToCrop.push({ file, previewUrl: URL.createObjectURL(file) });
      }
    });

    if (errors.length) setGlobalError(errors.join('\n'));
    if (newItems.length) setQueue((prev) => [...prev, ...newItems]);
    if (imagesToCrop.length) setCropQueue((prev) => [...prev, ...imagesToCrop]);
    // Reset input so the same files can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Gallery crop handlers ──────────────────────────────────────
  const handleGalleryCropConfirm = (blob, previewUrl) => {
    const original = cropQueue[0];
    const file = new File([blob], original.file.name, { type: 'image/jpeg' });
    setQueue((prev) => [...prev, {
      id: nextId.current++,
      file,
      isVideo: false,
      previewUrl,
      caption: '',
      thumbFile: null,
      thumbPreview: null,
      status: 'idle',
      errorMsg: '',
    }]);
    setCropQueue((prev) => prev.slice(1)); // move to next
  };

  const handleGalleryCropSkip = () => {
    // Skip crop — add original as-is
    const original = cropQueue[0];
    setQueue((prev) => [...prev, {
      id: nextId.current++,
      file: original.file,
      isVideo: false,
      previewUrl: original.previewUrl,
      caption: '',
      thumbFile: null,
      thumbPreview: null,
      status: 'idle',
      errorMsg: '',
    }]);
    setCropQueue((prev) => prev.slice(1));
  };

  const handleGalleryCropCancel = () => {
    // Discard all remaining crop queue items
    setCropQueue([]);
  };

  // Drag-and-drop support
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    const fakeEvent = { target: { files }, preventDefault: () => {} };
    handleFilesPicked(fakeEvent);
  };

  const handleDragOver = (e) => e.preventDefault();

  // ── Queue item helpers ─────────────────────────────────────────
  const removeFromQueue = (id) => setQueue((prev) => prev.filter((i) => i.id !== id));

  const updateCaption = (id, caption) =>
    setQueue((prev) => prev.map((i) => i.id === id ? { ...i, caption } : i));

  const updateThumb = (id, file, previewUrl) =>
    setQueue((prev) => prev.map((i) => i.id === id ? { ...i, thumbFile: file, thumbPreview: previewUrl } : i));

  const removeThumb = (id) =>
    setQueue((prev) => prev.map((i) => i.id === id ? { ...i, thumbFile: null, thumbPreview: null } : i));

  const clearDone = () => setQueue((prev) => prev.filter((i) => i.status !== 'done'));

  // ── Upload all idle items sequentially ────────────────────────
  const handleUploadAll = async () => {
    const idleItems = queue.filter((i) => i.status === 'idle');
    if (!idleItems.length) return;

    setUploading(true);
    setGlobalError(''); setGlobalSuccess('');

    for (const item of idleItems) {
      // Mark as uploading
      setQueue((prev) => prev.map((i) => i.id === item.id ? { ...i, status: 'uploading' } : i));

      const formData = new FormData();
      formData.append('media', item.file);
      formData.append('caption', item.caption);
      if (item.thumbFile) formData.append('thumbnail', item.thumbFile);

      try {
        const res = await fetch(`${API}/api/gallery/upload`, { method: 'POST', body: formData });
        const data = await res.json();
        if (data.success) {
          setQueue((prev) => prev.map((i) => i.id === item.id ? { ...i, status: 'done' } : i));
        } else {
          setQueue((prev) => prev.map((i) => i.id === item.id ? { ...i, status: 'error', errorMsg: data.message || 'Upload failed' } : i));
        }
      } catch {
        setQueue((prev) => prev.map((i) => i.id === item.id ? { ...i, status: 'error', errorMsg: 'Could not connect to server' } : i));
      }
    }

    setUploading(false);
    await fetchItems(); // refresh gallery

    // Show summary
    setQueue((prev) => {
      const done = prev.filter((i) => i.status === 'done').length;
      const failed = prev.filter((i) => i.status === 'error').length;
      if (done > 0) setGlobalSuccess(`${done} file${done > 1 ? 's' : ''} uploaded successfully.${failed > 0 ? ` ${failed} failed.` : ''}`);
      else if (failed > 0) setGlobalError(`${failed} file${failed > 1 ? 's' : ''} failed to upload.`);
      return prev;
    });
  };

  // ── Existing gallery operations ────────────────────────────────
  const handleDelete = async (filename, type) => {
    if (!window.confirm(`Delete this ${type}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/gallery/${filename}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) setItems((prev) => prev.filter((i) => i.filename !== filename));
      else setGlobalError(data.message);
    } catch { setGlobalError('Delete failed.'); }
  };

  const handleSaveCaption = async (filename) => {
    try {
      const res = await fetch(`${API}/api/gallery/${filename}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: editCaptionValue }),
      });
      const data = await res.json();
      if (data.success) {
        setItems((prev) => prev.map((i) => i.filename === filename ? { ...i, caption: editCaptionValue } : i));
        setEditingCaption(null);
      }
    } catch { setGlobalError('Caption update failed.'); }
  };

  // Called after thumbnail change/remove in modal
  const handleThumbnailChanged = (filename, newThumb) => {
    setItems((prev) => prev.map((i) => i.filename === filename ? { ...i, thumbnail: newThumb } : i));
  };

  const filtered = items.filter((i) => filter === 'all' || i.type === filter);
  const idleCount = queue.filter((i) => i.status === 'idle').length;
  const doneCount = queue.filter((i) => i.status === 'done').length;

  return (
    <>
      {/* ── Gallery Banner Card ── */}
      <div className="card">
        <h2>Gallery Page Banner</h2>
        <p className="section-desc">This image appears as the hero banner at the top of the public Gallery page.</p>

        {bannerError && <div className="error-msg">{bannerError}</div>}
        {bannerSuccess && <div className="success-msg">{bannerSuccess}</div>}

        {bannerLoading ? (
          <div className="banner-loading">Loading…</div>
        ) : bannerUrl ? (
          <div className="gb-preview-wrap">
            <img src={bannerUrl} alt="Gallery banner" className="gb-preview-img" />
            <div className="gb-preview-actions">
              <button
                className="btn-secondary"
                onClick={() => bannerInputRef.current.click()}
                disabled={bannerUploading || bannerDeleting}
              >
                {bannerUploading ? 'Uploading…' : 'Change Banner'}
              </button>
              <button
                className="btn-danger"
                onClick={handleBannerDelete}
                disabled={bannerUploading || bannerDeleting}
              >
                {bannerDeleting ? 'Removing…' : 'Remove Banner'}
              </button>
            </div>
          </div>
        ) : (
          <div
            className="upload-area"
            onClick={() => bannerInputRef.current.click()}
            style={{ cursor: 'pointer' }}
          >
            <div className="upload-icon">🖼️</div>
            <p className="upload-hint">Click to upload Gallery Banner</p>
            <p className="upload-sub">JPG, PNG or WEBP — recommended 1920×500px (wide banner)</p>
          </div>
        )}

        <input
          ref={bannerInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden-input"
          onChange={handleBannerFilePick}
        />
      </div>

      {/* Gallery banner crop modal */}
      {bannerCropSrc && (
        <ImageCropModal
          imageSrc={bannerCropSrc}
          aspect={16 / 5}
          title="Crop Gallery Banner"
          onCancel={handleBannerCropCancel}
          onConfirm={handleBannerCropConfirm}
          outputWidth={1920}
          outputHeight={600}
        />
      )}

      {/* Stats */}
      <div className="gallery-stats-row">
        <div className="g-stat-admin"><span className="g-stat-num">{items.length}</span><span>Total</span></div>
        <div className="g-stat-admin"><span className="g-stat-num">{items.filter(i => i.type === 'photo').length}</span><span>Photos</span></div>
        <div className="g-stat-admin"><span className="g-stat-num">{items.filter(i => i.type === 'video').length}</span><span>Videos</span></div>
      </div>

      {/* ── Upload Queue Card ── */}
      <div className="card">
        <h2>Upload Photos &amp; Videos</h2>
        <div className="g-upload-requirements">
          <span>📷 Images: JPG, PNG, WEBP, GIF — max 100MB each</span>
          <span>🎬 Videos: MP4, WEBM, MOV, AVI — max 100MB each</span>
          <span>Select multiple files at once</span>
        </div>

        {/* Drop zone */}
        <div
          className="upload-area gallery-upload-area multi-drop-zone"
          onClick={() => fileInputRef.current.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div className="upload-icon">📁</div>
          <p className="upload-hint">Click or drag &amp; drop to add files</p>
          <p className="upload-sub">{GALLERY_ALLOWED_LABEL} — select multiple</p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime,video/x-msvideo,.png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.mov,.avi"
          className="hidden-input"
          multiple
          onChange={handleFilesPicked}
        />

        {/* Queue list */}
        {queue.length > 0 && (
          <>
            <div className="queue-header-row">
              <span className="queue-count-label">
                {queue.length} file{queue.length > 1 ? 's' : ''} in queue
                {idleCount > 0 && ` · ${idleCount} ready`}
                {doneCount > 0 && ` · ${doneCount} done`}
              </span>
              <div className="queue-header-actions">
                {doneCount > 0 && (
                  <button className="btn btn-xs btn-secondary" onClick={clearDone}>Clear done</button>
                )}
                {queue.length > 0 && !uploading && (
                  <button className="btn btn-xs btn-secondary" onClick={() => setQueue([])}>Clear all</button>
                )}
              </div>
            </div>

            <div className="queue-list">
              {queue.map((item) => (
                <QueueItem
                  key={item.id}
                  item={item}
                  onRemove={removeFromQueue}
                  onCaptionChange={updateCaption}
                  onThumbChange={updateThumb}
                  onThumbRemove={removeThumb}
                />
              ))}
            </div>
          </>
        )}

        {globalError && <div className="alert alert-error" style={{ whiteSpace: 'pre-line' }}>{globalError}</div>}
        {globalSuccess && <div className="alert alert-success">{globalSuccess}</div>}

        <div className="upload-actions" style={{ marginTop: 16 }}>
          <button
            className="btn btn-primary"
            onClick={handleUploadAll}
            disabled={idleCount === 0 || uploading}
          >
            {uploading ? 'Uploading...' : `Upload ${idleCount > 0 ? idleCount : ''} File${idleCount !== 1 ? 's' : ''}`}
          </button>
          {queue.length > 0 && !uploading && (
            <button className="btn btn-secondary" onClick={() => { setQueue([]); setGlobalError(''); setGlobalSuccess(''); }}>
              Cancel All
            </button>
          )}
        </div>
      </div>

      {/* ── Gallery Grid Card ── */}
      <div className="card">
        <div className="gallery-header">
          <h2>Gallery Items</h2>
          <div className="g-filters">
            {['all', 'photo', 'video'].map((f) => (
              <button key={f} className={`g-filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'photo' ? 'Photos' : 'Videos'}
              </button>
            ))}
          </div>
        </div>

        {loadingItems ? (
          <div className="gallery-loading-admin"><div className="spinner" /><p>Loading...</p></div>
        ) : filtered.length === 0 ? (
          <div className="gallery-empty-admin">
            <p>{items.length === 0 ? 'No items uploaded yet.' : `No ${filter}s found.`}</p>
          </div>
        ) : (
          <div className="admin-gallery-grid">
            {filtered.map((item, index) => (
              <div className="admin-g-card" key={item.filename}>
                {/* Thumbnail */}
                <div className="admin-g-thumb" onClick={() => setLightboxIndex(index)}>
                  {item.type === 'photo' ? (
                    <img src={`${API}/gallery/${item.filename}`} alt={item.caption || item.filename} loading="lazy" />
                  ) : item.thumbnail ? (
                    <img src={`${API}/gallery/${item.thumbnail}`} alt="Video thumbnail" loading="lazy" />
                  ) : (
                    <video src={`${API}/gallery/${item.filename}`} muted preload="metadata" />
                  )}
                  <div className="admin-g-overlay">
                    <span>{item.type === 'video' ? '▶' : '🔍'}</span>
                  </div>
                  {item.type === 'video' && (
                    <div className={`admin-g-badge ${item.thumbnail ? 'has-thumb' : ''}`}>
                      {item.thumbnail ? '🖼 VIDEO' : 'VIDEO'}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="admin-g-info">
                  <div className="admin-g-meta">
                    <span className={`type-badge ${item.type}`}>{item.type.toUpperCase()}</span>
                    <span className="admin-g-size">{formatBytes(item.size)}</span>
                  </div>

                  {/* Caption edit */}
                  {editingCaption === item.filename ? (
                    <div className="caption-edit-row">
                      <input
                        type="text"
                        value={editCaptionValue}
                        onChange={(e) => setEditCaptionValue(e.target.value)}
                        className="caption-input"
                        maxLength={120}
                        autoFocus
                      />
                      <button className="btn btn-xs btn-primary" onClick={() => handleSaveCaption(item.filename)}>Save</button>
                      <button className="btn btn-xs btn-secondary" onClick={() => setEditingCaption(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="caption-display" onClick={() => { setEditingCaption(item.filename); setEditCaptionValue(item.caption || ''); }}>
                      {item.caption ? <span>{item.caption}</span> : <span className="no-caption">+ Add caption</span>}
                      <span className="edit-icon">✏️</span>
                    </div>
                  )}

                  {/* Change thumbnail — only for videos */}
                  {item.type === 'video' && (
                    <button
                      className="btn btn-sm btn-change-thumb w-full"
                      onClick={() => setThumbModal(item)}
                    >
                      🖼️ {item.thumbnail ? 'Change Thumbnail' : 'Add Thumbnail'}
                    </button>
                  )}

                  <div className="admin-g-date">
                    {new Date(item.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>

                  <button className="btn btn-danger btn-sm w-full" onClick={() => handleDelete(item.filename, item.type)}>
                    🗑 Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && filtered[lightboxIndex] && (() => {
        const lb = filtered[lightboxIndex];
        const goPrev = () => setLightboxIndex((i) => (i > 0 ? i - 1 : filtered.length - 1));
        const goNext = () => setLightboxIndex((i) => (i < filtered.length - 1 ? i + 1 : 0));
        return (
          <div className="admin-lightbox" onClick={() => setLightboxIndex(null)}>
            <div className="admin-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close-btn" onClick={() => setLightboxIndex(null)}>✕</button>
              <div className="admin-lightbox-counter">{lightboxIndex + 1} / {filtered.length}</div>
              <button className="admin-lb-nav admin-lb-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }}>&#8249;</button>
              {lb.type === 'photo' ? (
                <img key={lb.filename} src={`${API}/gallery/${lb.filename}`} alt={lb.caption} className="lightbox-media" />
              ) : (
                <video key={lb.filename} src={`${API}/gallery/${lb.filename}`} controls autoPlay className="lightbox-media" />
              )}
              <button className="admin-lb-nav admin-lb-next" onClick={(e) => { e.stopPropagation(); goNext(); }}>&#8250;</button>
              {lb.caption && <p className="lightbox-caption-admin">{lb.caption}</p>}
            </div>
          </div>
        );
      })()}

      {/* Change Thumbnail Modal */}
      {thumbModal && (
        <ChangeThumbnailModal
          item={thumbModal}
          onClose={() => setThumbModal(null)}
          onChanged={handleThumbnailChanged}
        />
      )}

      {/* Gallery image crop modal — shown one at a time per queued image */}
      {cropQueue.length > 0 && (
        <ImageCropModal
          imageSrc={cropQueue[0].previewUrl}
          aspect={null}
          title={`Crop Image${cropQueue.length > 1 ? ` (${cropQueue.length} remaining)` : ''}`}
          onCancel={handleGalleryCropCancel}
          onConfirm={handleGalleryCropConfirm}
          outputWidth={1920}
          extraActions={
            <button className="crop-btn-skip" onClick={handleGalleryCropSkip}>
              Skip Crop
            </button>
          }
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Service Pages Section ────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
const SERVICE_LIST = [
  { slug: 'borewell-drilling',  label: 'Borewell Drilling',                icon: '🔩' },
  { slug: 'borewell-cleaning',  label: 'Borewell Cleaning',                icon: '🧹' },
  { slug: 'slow-rig-borewells', label: 'Slow Rig Borewells',               icon: '⚙️' },
  { slug: 'pump-sales-service', label: 'Pump Sales & Service',             icon: '🚰' },
  { slug: 'soil-test',          label: 'Soil Test',                        icon: '🧪' },
  { slug: 'plumbing-material',  label: 'Plumbing Material Supply',         icon: '🔧' },
  { slug: 'pile-foundation',    label: 'Pile Foundation',                  icon: '🏛️' },
  { slug: 'dewatering',         label: 'Dewatering',                       icon: '💧' },
];

const SERVICE_IMG_ALLOWED = ['image/png', 'image/jpeg', 'image/webp'];
const SERVICE_IMG_MAX_MB = 10;
const SERVICE_IMG_MAX_BYTES = SERVICE_IMG_MAX_MB * 1024 * 1024;

// Per-service fixed image slot definitions (what each layout position expects)
const SERVICE_IMG_SLOTS = {
  'borewell-drilling': [
    { label: 'Image 1 — Right-side intro photo (tall)', size: '600 × 420 px', hint: 'Appears beside the intro text on the right. Use a portrait or square drilling site photo.' },
    { label: 'Image 2 — Below intro (landscape)', size: '900 × 380 px', hint: 'Wide landscape image shown below the intro block. Drilling rig or borehole in action.' },
    { label: 'Image 3 — Timeline accent (square)', size: '480 × 480 px', hint: 'Shown beside the process timeline. Close-up of drilling equipment.' },
    { label: 'Image 4 — Features section header', size: '1100 × 320 px', hint: 'Wide strip image above the features cards. A site overview or team photo.' },
  ],
  'borewell-cleaning': [
    { label: 'Image 1 — Before cleaning (square)', size: '480 × 480 px', hint: 'Shows the condition before cleaning. Displayed in a 2-column split layout.' },
    { label: 'Image 2 — After cleaning (square)', size: '480 × 480 px', hint: 'Shows the result after cleaning. Displayed beside Image 1.' },
    { label: 'Image 3 — Process photo (landscape)', size: '900 × 400 px', hint: 'Full-width image below the before/after pair. Cleaning equipment or team at work.' },
  ],
  'slow-rig-borewells': [
    { label: 'Image 1 — Slow rig machine (landscape)', size: '800 × 480 px', hint: 'Main photo of the slow rig equipment. Shown in the dark blueprint section.' },
    { label: 'Image 2 — Drilling close-up (square)', size: '480 × 480 px', hint: 'Close-up of the drill bit or casing. Placed beside the process cards.' },
    { label: 'Image 3 — Site overview (landscape)', size: '1100 × 360 px', hint: 'Wide panoramic site photo. Shown as a full-width strip below process steps.' },
  ],
  'pump-sales-service': [
    { label: 'Image 1 — Pump product shot (square)', size: '480 × 480 px', hint: 'Clean product photo of a pump. Placed in the intro section.' },
    { label: 'Image 2 — Installation photo (landscape)', size: '800 × 450 px', hint: 'Technician installing a pump. Shown beside the brand badges area.' },
    { label: 'Image 3 — Service/repair photo (square)', size: '480 × 480 px', hint: 'Pump servicing or repair in progress. Placed in the process section.' },
    { label: 'Image 4 — Showroom or warehouse (landscape)', size: '1100 × 320 px', hint: 'Wide photo of your showroom or stock. Shown above the CTA.' },
  ],
  'soil-test': [
    { label: 'Image 1 — Lab/testing equipment (square)', size: '480 × 480 px', hint: 'Soil testing equipment or lab. Placed in the gradient intro box.' },
    { label: 'Image 2 — Field sampling photo (landscape)', size: '800 × 400 px', hint: 'Team doing fieldwork or soil sampling. Shown in a split layout.' },
    { label: 'Image 3 — Report / results (square)', size: '480 × 480 px', hint: 'A soil test report or result chart. Placed beside the process grid.' },
  ],
  'dewatering': [
    { label: 'Image 1 — Pump setup (landscape)', size: '800 × 480 px', hint: 'Dewatering pump setup on site. Used for alternating row 1 (left).' },
    { label: 'Image 2 — Water being pumped (landscape)', size: '800 × 480 px', hint: 'Active dewatering in progress. Used for alternating row 2 (right).' },
    { label: 'Image 3 — Construction site (landscape)', size: '800 × 480 px', hint: 'Dry excavation after dewatering. Used for alternating row 3 (left).' },
    { label: 'Image 4 — Equipment / pipes (landscape)', size: '800 × 480 px', hint: 'Suction pipes or hose layout. Used for alternating row 4 (right).' },
  ],
  'plumbing-material': [
    { label: 'Image 1 — Pipe stock / variety (landscape)', size: '800 × 400 px', hint: 'Range of pipes/fittings displayed. Shown in the category section.' },
    { label: 'Image 2 — Valves / fittings close-up (square)', size: '480 × 480 px', hint: 'Close-up of valves or fittings. Placed beside the category boxes.' },
    { label: 'Image 3 — Installation in progress (landscape)', size: '1100 × 360 px', hint: 'Plumbing installation photo. Full-width strip below the process list.' },
  ],
  'pile-foundation': [
    { label: 'Image 1 — Pile rig machine (portrait)', size: '480 × 640 px', hint: 'Tall portrait photo of the pile rig. Placed beside the stat boxes.' },
    { label: 'Image 2 — Pile cage / reinforcement (square)', size: '480 × 480 px', hint: 'Rebar cage or pile reinforcement. Shown in the process circles section.' },
    { label: 'Image 3 — Completed piles (landscape)', size: '900 × 380 px', hint: 'Finished pile caps or foundation. Wide strip below the circles.' },
  ],
};

function ServicePagesSection() {
  const [selectedSlug, setSelectedSlug] = useState(SERVICE_LIST[0].slug);
  const [activeTab, setActiveTab] = useState('banner'); // banner | content | images
  const [draft, setDraft] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Banner upload states
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerInputRef = useRef();
  const [bannerCropSrc, setBannerCropSrc] = useState(null); // raw src for crop modal

  // Image upload states
  const [imgFiles, setImgFiles] = useState([]);
  const [imgUploading, setImgUploading] = useState(false);
  const imgInputRef = useRef();
  // Content image crop queue
  const [imgCropQueue, setImgCropQueue] = useState([]); // [{file, previewUrl}, ...]

  // Content form state
  const [form, setForm] = useState({});

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const fetchDraft = useCallback(async (slug) => {
    setLoadingDraft(true);
    setBannerFile(null); setBannerPreview(null); setImgFiles([]);
    try {
      const res = await fetch(`${API}/api/services/${slug}/draft`);
      const data = await res.json();
      if (data.success) {
        setDraft(data.data);
        setForm({
          title: data.data.title || '',
          subtitle: data.data.subtitle || '',
          intro: data.data.intro || '',
          intro2: data.data.intro2 || '',
          processTitle: data.data.processTitle || '',
          featuresTitle: data.data.featuresTitle || '',
          specsTitle: data.data.specsTitle || '',
          ctaTitle: data.data.ctaTitle || '',
          ctaText: data.data.ctaText || '',
          processSteps: data.data.processSteps || [],
          features: data.data.features || [],
          specs: data.data.specs || [],
        });
      }
    } catch { showMsg('error', 'Could not load service data.'); }
    finally { setLoadingDraft(false); }
  }, []);

  useEffect(() => { fetchDraft(selectedSlug); }, [selectedSlug, fetchDraft]);

  // ── Banner upload ──────────────────────────────────────────────
  const handleBannerDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    validateBanner(file);
  };

  const validateBanner = (file) => {
    if (!file) return;
    if (!SERVICE_IMG_ALLOWED.includes(file.type)) { showMsg('error', 'Only PNG, JPG, WEBP allowed.'); return; }
    if (file.size > SERVICE_IMG_MAX_BYTES) { showMsg('error', `File too large. Max ${SERVICE_IMG_MAX_MB}MB.`); return; }
    // Open crop modal instead of setting directly
    setBannerCropSrc(URL.createObjectURL(file));
    if (bannerInputRef.current) bannerInputRef.current.value = '';
  };

  const handleBannerCropConfirm = (blob, previewUrl) => {
    const file = new File([blob], 'banner.jpg', { type: 'image/jpeg' });
    setBannerFile(file);
    setBannerPreview(previewUrl);
    setBannerCropSrc(null);
  };

  const handleBannerCropCancel = () => setBannerCropSrc(null);

  const handleUploadBanner = async () => {
    if (!bannerFile) return;
    setBannerUploading(true);
    const fd = new FormData();
    fd.append('banner', bannerFile);
    try {
      const res = await fetch(`${API}/api/services/${selectedSlug}/banner`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        showMsg('success', 'Banner uploaded to draft.');
        setBannerFile(null); setBannerPreview(null);
        if (bannerInputRef.current) bannerInputRef.current.value = '';
        fetchDraft(selectedSlug);
      } else { showMsg('error', data.message || 'Upload failed.'); }
    } catch { showMsg('error', 'Could not connect to server.'); }
    finally { setBannerUploading(false); }
  };

  const handleRemoveBanner = async () => {
    if (!window.confirm('Remove the banner for this service page?')) return;
    try {
      const res = await fetch(`${API}/api/services/${selectedSlug}/banner`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { showMsg('success', 'Banner removed.'); fetchDraft(selectedSlug); }
      else showMsg('error', data.message);
    } catch { showMsg('error', 'Could not connect.'); }
  };

  // ── Image upload ───────────────────────────────────────────────
  const handleImgsPicked = (e) => {
    const files = Array.from(e.target.files).filter((f) => {
      if (!SERVICE_IMG_ALLOWED.includes(f.type)) { showMsg('error', `"${f.name}" is not a valid image type.`); return false; }
      if (f.size > SERVICE_IMG_MAX_BYTES) { showMsg('error', `"${f.name}" is too large (max ${SERVICE_IMG_MAX_MB}MB).`); return false; }
      return true;
    });
    // Route images through crop queue
    if (files.length) {
      setImgCropQueue((prev) => [...prev, ...files.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }))]);
    }
    if (imgInputRef.current) imgInputRef.current.value = '';
  };

  // ── Service content image crop handlers ───────────────────────
  const handleImgCropConfirm = (blob, previewUrl) => {
    const original = imgCropQueue[0];
    const file = new File([blob], original.file.name, { type: 'image/jpeg' });
    setImgFiles((prev) => [...prev, { file, preview: previewUrl }]);
    setImgCropQueue((prev) => prev.slice(1));
  };

  const handleImgCropSkip = () => {
    const original = imgCropQueue[0];
    setImgFiles((prev) => [...prev, { file: original.file, preview: original.previewUrl }]);
    setImgCropQueue((prev) => prev.slice(1));
  };

  const handleImgCropCancel = () => setImgCropQueue([]);

  const handleUploadImages = async () => {
    if (!imgFiles.length) return;
    setImgUploading(true);
    const fd = new FormData();
    imgFiles.forEach((item) => fd.append('images', item.file));
    try {
      const res = await fetch(`${API}/api/services/${selectedSlug}/images`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        showMsg('success', `${data.images.length} image(s) added to draft.`);
        setImgFiles([]);
        fetchDraft(selectedSlug);
      } else { showMsg('error', data.message || 'Upload failed.'); }
    } catch { showMsg('error', 'Could not connect.'); }
    finally { setImgUploading(false); }
  };

  const handleDeleteImage = async (filename) => {
    if (!window.confirm('Remove this image?')) return;
    try {
      const res = await fetch(`${API}/api/services/${selectedSlug}/images/${filename}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { showMsg('success', 'Image removed.'); fetchDraft(selectedSlug); }
      else showMsg('error', data.message);
    } catch { showMsg('error', 'Could not connect.'); }
  };

  // ── Save draft content ─────────────────────────────────────────
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/services/${selectedSlug}/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { showMsg('success', 'Draft saved!'); fetchDraft(selectedSlug); }
      else showMsg('error', data.message || 'Save failed.');
    } catch { showMsg('error', 'Could not connect.'); }
    finally { setSaving(false); }
  };

  // ── Preview ────────────────────────────────────────────────────
  const handlePreview = async () => {
    try {
      const res = await fetch(`${API}/api/services/${selectedSlug}/draft`);
      const data = await res.json();
      if (data.success) { setPreviewData(data.data); setPreviewOpen(true); }
    } catch { showMsg('error', 'Could not load preview.'); }
  };

  // ── Publish ────────────────────────────────────────────────────
  const handlePublish = async () => {
    if (!window.confirm('Publish this draft? It will immediately be visible to customers.')) return;
    setPublishing(true);
    try {
      const res = await fetch(`${API}/api/services/${selectedSlug}/publish`, { method: 'POST' });
      const data = await res.json();
      if (data.success) showMsg('success', 'Published! Page is now live.');
      else showMsg('error', data.message || 'Publish failed.');
    } catch { showMsg('error', 'Could not connect.'); }
    finally { setPublishing(false); }
  };

  // ── Form helpers ───────────────────────────────────────────────
  const updateStep = (idx, field, val) => {
    const steps = [...(form.processSteps || [])];
    steps[idx] = { ...steps[idx], [field]: val };
    setForm((f) => ({ ...f, processSteps: steps }));
  };

  const updateFeature = (idx, field, val) => {
    const features = [...(form.features || [])];
    features[idx] = { ...features[idx], [field]: val };
    setForm((f) => ({ ...f, features }));
  };

  const updateSpec = (idx, colIdx, val) => {
    const specs = [...(form.specs || [])];
    specs[idx] = [...specs[idx]];
    specs[idx][colIdx] = val;
    setForm((f) => ({ ...f, specs }));
  };

  const addStep = () => {
    const steps = [...(form.processSteps || [])];
    steps.push({ step: steps.length + 1, title: '', desc: '' });
    setForm((f) => ({ ...f, processSteps: steps }));
  };
  const removeStep = (idx) => {
    const steps = (form.processSteps || []).filter((_, i) => i !== idx)
      .map((s, i) => ({ ...s, step: i + 1 }));
    setForm((f) => ({ ...f, processSteps: steps }));
  };

  const addFeature = () => {
    const features = [...(form.features || [])];
    features.push({ icon: '', title: '', desc: '' });
    setForm((f) => ({ ...f, features }));
  };
  const removeFeature = (idx) => {
    const features = (form.features || []).filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, features }));
  };

  const addSpec = () => {
    const specs = [...(form.specs || [])];
    specs.push(['', '']);
    setForm((f) => ({ ...f, specs }));
  };
  const removeSpec = (idx) => {
    const specs = (form.specs || []).filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, specs }));
  };

  if (loadingDraft) {
    return <div className="card" style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /><p>Loading...</p></div>;
  }

  return (
    <>
      {/* Service Picker */}
      <div className="card sp-admin-picker-card">
        <h2>Select Service Page</h2>
        <div className="sp-admin-picker-grid">
          {SERVICE_LIST.map((s) => (
            <button
              key={s.slug}
              className={`sp-admin-picker-btn ${selectedSlug === s.slug ? 'active' : ''}`}
              onClick={() => { setSelectedSlug(s.slug); setActiveTab('banner'); }}
            >
              <span>{s.icon}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Action bar */}
      <div className="sp-admin-action-bar">
        <div className="sp-admin-tabs">
          {['banner', 'content', 'images'].map((tab) => (
            <button
              key={tab}
              className={`sp-tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'banner' ? '🖼️ Banner' : tab === 'content' ? '📝 Content' : '📷 Images'}
            </button>
          ))}
        </div>
        <div className="sp-admin-actions">
          <button className="btn btn-secondary" onClick={handlePreview}>👁 Preview</button>
          <button className="btn btn-primary" onClick={handleSaveDraft} disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Draft'}
          </button>
          <button className="btn btn-publish" onClick={handlePublish} disabled={publishing}>
            {publishing ? 'Publishing...' : '🚀 Publish'}
          </button>
        </div>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}

      {/* ── BANNER TAB ── */}
      {activeTab === 'banner' && (
        <div className="card">
          <h2>Service Banner Image</h2>
          <p className="sp-tab-desc">This banner appears at the top of the service page, below the navbar. Recommended: 1920×600px, PNG/JPG/WEBP, max 10MB.</p>
          {draft?.bannerUrl && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Current Banner (in draft):</p>
              <div className="banner-preview-wrap">
                <img src={draft.bannerUrl} alt="Service banner" className="banner-preview-img" />
              </div>
              <button className="btn btn-danger" style={{ marginTop: 10 }} onClick={handleRemoveBanner}>Remove Banner</button>
            </div>
          )}
          <div
            className="upload-area"
            style={{ minHeight: 160 }}
            onClick={() => bannerInputRef.current.click()}
            onDrop={handleBannerDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {bannerPreview ? (
              <img src={bannerPreview} alt="Preview" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 8 }} />
            ) : (
              <>
                <div className="upload-icon">🖼️</div>
                <p className="upload-hint">Click to browse or drag &amp; drop a banner image</p>
                <p className="upload-sub">PNG, JPG, WEBP — max 10MB — recommended 1920×600px</p>
              </>
            )}
          </div>
          <input ref={bannerInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden-input" onChange={(e) => validateBanner(e.target.files[0])} />
          {bannerFile && (
            <div className="file-info">
              <span>📄 {bannerFile.name}</span>
              <span>{formatBytes(bannerFile.size)}</span>
            </div>
          )}
          <div className="upload-actions" style={{ marginTop: 12 }}>
            <button className="btn btn-primary" onClick={handleUploadBanner} disabled={!bannerFile || bannerUploading}>
              {bannerUploading ? 'Uploading...' : 'Upload Banner'}
            </button>
            {bannerFile && (
              <button className="btn btn-secondary" onClick={() => { setBannerFile(null); setBannerPreview(null); if (bannerInputRef.current) bannerInputRef.current.value = ''; }}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── IMAGES TAB ── */}
      {activeTab === 'images' && (() => {
        const slots = SERVICE_IMG_SLOTS[selectedSlug] || [];
        const uploadedImages = draft?.images || [];
        const fixedImages = uploadedImages.slice(0, slots.length);
        const additionalImages = uploadedImages.slice(slots.length);
        return (
          <div className="card">
            <h2>Content Images</h2>
            <p className="sp-tab-desc">
              This page has <strong>{slots.length} fixed layout image slots</strong> — each slot appears in a specific position in the design.
              Upload images in order. After the fixed slots, any extra images appear as "Additional Service Images" at the bottom.
            </p>

            {/* ── Fixed image slots guide ── */}
            <div className="sp-img-slots-guide">
              <h3>Image Layout Slots</h3>
              {slots.map((slot, idx) => {
                const uploaded = fixedImages[idx];
                return (
                  <div key={idx} className={`sp-img-slot-row ${uploaded ? 'sp-img-slot-filled' : 'sp-img-slot-empty'}`}>
                    <div className="sp-img-slot-info">
                      <span className="sp-img-slot-num">{idx + 1}</span>
                      <div>
                        <strong>{slot.label}</strong>
                        <span className="sp-img-slot-size">Recommended: {slot.size}</span>
                        <span className="sp-img-slot-hint">{slot.hint}</span>
                      </div>
                    </div>
                    <div className="sp-img-slot-preview">
                      {uploaded ? (
                        <div className="sp-img-slot-thumb">
                          <img src={uploaded.url} alt={uploaded.caption || slot.label} />
                          <div className="sp-img-slot-actions">
                            <span className="sp-img-slot-ok">✓ Uploaded</span>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteImage(uploaded.filename)}>Remove</button>
                          </div>
                        </div>
                      ) : (
                        <div className="sp-img-slot-missing">No image yet — upload below in order</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Additional images ── */}
            {additionalImages.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <h3 style={{ color: '#1a3a5c', marginBottom: 12 }}>Additional Service Images ({additionalImages.length})</h3>
                <p style={{ fontSize: '0.87rem', color: '#718096', marginBottom: 14 }}>These appear in the "Additional Service Images" gallery section at the bottom of the page.</p>
                <div className="sp-img-admin-grid">
                  {additionalImages.map((img) => (
                    <div key={img.filename} className="sp-img-admin-card">
                      <img src={img.url} alt={img.caption || img.filename} />
                      <p className="sp-img-admin-caption">{img.caption || <em>No caption</em>}</p>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteImage(img.filename)}>🗑 Remove</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Upload new images ── */}
            <div style={{ marginTop: 28 }}>
              <h3 style={{ color: '#1a3a5c', marginBottom: 8 }}>
                {uploadedImages.length < slots.length
                  ? `Upload Slot ${uploadedImages.length + 1} of ${slots.length}: ${slots[uploadedImages.length]?.label || ''}`
                  : 'Upload Additional Images'}
              </h3>
              {uploadedImages.length < slots.length && (
                <p style={{ fontSize: '0.84rem', color: '#f0a500', fontWeight: 600, marginBottom: 10 }}>
                  Recommended size: {slots[uploadedImages.length]?.size} — {slots[uploadedImages.length]?.hint}
                </p>
              )}
              <div
                className="upload-area"
                onClick={() => imgInputRef.current.click()}
                onDrop={(e) => { e.preventDefault(); const files = Array.from(e.dataTransfer.files); const fakeEv = { target: { files } }; handleImgsPicked(fakeEv); }}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="upload-icon">📷</div>
                <p className="upload-hint">Click or drag &amp; drop images</p>
                <p className="upload-sub">PNG, JPG, WEBP — max 10MB each — multiple allowed</p>
              </div>
              <input ref={imgInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden-input" multiple onChange={handleImgsPicked} />

              {imgFiles.length > 0 && (
                <div className="sp-img-admin-grid" style={{ marginTop: 12 }}>
                  {imgFiles.map((item, idx) => (
                    <div key={idx} className="sp-img-admin-card">
                      <img src={item.preview} alt={item.file.name} />
                      <p className="sp-img-admin-caption">{item.file.name}</p>
                      <button className="btn btn-danger btn-sm" onClick={() => setImgFiles((prev) => prev.filter((_, i) => i !== idx))}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

              {imgFiles.length > 0 && (
                <div className="upload-actions" style={{ marginTop: 12 }}>
                  <button className="btn btn-primary" onClick={handleUploadImages} disabled={imgUploading}>
                    {imgUploading ? 'Uploading...' : `Upload ${imgFiles.length} Image(s)`}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setImgFiles([])}>Clear</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── CONTENT TAB ── */}
      {activeTab === 'content' && (
        <div className="card">
          <h2>Page Content</h2>
          <p className="sp-tab-desc">Edit the text content for this service page. Click "Save Draft" when done, then "Preview" to review before publishing.</p>

          <div className="sp-content-form">
            <label>Page Title</label>
            <input type="text" value={form.title || ''} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={100} />

            <label>Subtitle</label>
            <input type="text" value={form.subtitle || ''} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} maxLength={200} />

            <label>Introduction (paragraph 1)</label>
            <textarea rows={4} value={form.intro || ''} onChange={(e) => setForm((f) => ({ ...f, intro: e.target.value }))} />

            <label>Introduction (paragraph 2)</label>
            <textarea rows={3} value={form.intro2 || ''} onChange={(e) => setForm((f) => ({ ...f, intro2: e.target.value }))} />

            <label>Process Section Title</label>
            <input type="text" value={form.processTitle || ''} onChange={(e) => setForm((f) => ({ ...f, processTitle: e.target.value }))} />

            <div className="sp-field-section-header">
              <span className="sp-field-section-label">Process Steps ({(form.processSteps || []).length})</span>
              <button type="button" className="sp-add-btn" onClick={addStep}>+ Add Step</button>
            </div>
            {(form.processSteps || []).map((step, idx) => (
              <div key={idx} className="sp-step-row">
                <span className="sp-step-num">Step {step.step}</span>
                <input type="text" placeholder="Step title" value={step.title || ''} onChange={(e) => updateStep(idx, 'title', e.target.value)} style={{ flex: 1 }} />
                <input type="text" placeholder="Step description" value={step.desc || ''} onChange={(e) => updateStep(idx, 'desc', e.target.value)} style={{ flex: 2 }} />
                <button type="button" className="sp-remove-btn" onClick={() => removeStep(idx)} title="Remove this step">−</button>
              </div>
            ))}

            <label>Features Section Title</label>
            <input type="text" value={form.featuresTitle || ''} onChange={(e) => setForm((f) => ({ ...f, featuresTitle: e.target.value }))} />

            <div className="sp-field-section-header">
              <span className="sp-field-section-label">Features ({(form.features || []).length})</span>
              <button type="button" className="sp-add-btn" onClick={addFeature}>+ Add Feature</button>
            </div>
            {(form.features || []).map((feat, idx) => (
              <div key={idx} className="sp-step-row">
                <input type="text" placeholder="Icon" value={feat.icon || ''} onChange={(e) => updateFeature(idx, 'icon', e.target.value)} style={{ width: 60 }} />
                <input type="text" placeholder="Title" value={feat.title || ''} onChange={(e) => updateFeature(idx, 'title', e.target.value)} style={{ flex: 1 }} />
                <input type="text" placeholder="Description" value={feat.desc || ''} onChange={(e) => updateFeature(idx, 'desc', e.target.value)} style={{ flex: 2 }} />
                <button type="button" className="sp-remove-btn" onClick={() => removeFeature(idx)} title="Remove this feature">−</button>
              </div>
            ))}

            <label>Specs Section Title</label>
            <input type="text" value={form.specsTitle || ''} onChange={(e) => setForm((f) => ({ ...f, specsTitle: e.target.value }))} />

            <div className="sp-field-section-header">
              <span className="sp-field-section-label">Specs ({(form.specs || []).length})</span>
              <button type="button" className="sp-add-btn" onClick={addSpec}>+ Add Spec</button>
            </div>
            {(form.specs || []).map((spec, idx) => (
              <div key={idx} className="sp-step-row">
                <input type="text" placeholder="Parameter" value={spec[0] || ''} onChange={(e) => updateSpec(idx, 0, e.target.value)} style={{ flex: 1 }} />
                <input type="text" placeholder="Value" value={spec[1] || ''} onChange={(e) => updateSpec(idx, 1, e.target.value)} style={{ flex: 1 }} />
                <button type="button" className="sp-remove-btn" onClick={() => removeSpec(idx)} title="Remove this spec">−</button>
              </div>
            ))}

            <label>CTA Heading</label>
            <input type="text" value={form.ctaTitle || ''} onChange={(e) => setForm((f) => ({ ...f, ctaTitle: e.target.value }))} />

            <label>CTA Sub-text</label>
            <input type="text" value={form.ctaText || ''} onChange={(e) => setForm((f) => ({ ...f, ctaText: e.target.value }))} />
          </div>

          <div className="upload-actions" style={{ marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Draft'}
            </button>
            <button className="btn btn-secondary" onClick={handlePreview}>👁 Preview</button>
            <button className="btn btn-publish" onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publishing...' : '🚀 Publish'}
            </button>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      {previewOpen && previewData && (
        <div className="sp-preview-modal-backdrop" onClick={() => setPreviewOpen(false)}>
          <div className="sp-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-preview-modal-header">
              <h3>Preview — {previewData.title}</h3>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: 20 }}>Draft Preview</span>
                <button className="modal-close-btn" onClick={() => setPreviewOpen(false)}>✕</button>
              </div>
            </div>
            <div className="sp-preview-modal-body">
              {/* Banner */}
              {previewData.bannerUrl ? (
                <div className="sp-preview-banner" style={{ backgroundImage: `url(${previewData.bannerUrl})` }}>
                  <div className="sp-preview-banner-overlay">
                    <h1>{previewData.title}</h1>
                    <p>{previewData.subtitle}</p>
                  </div>
                </div>
              ) : (
                <div className="sp-preview-banner sp-preview-banner-default">
                  <div className="sp-preview-banner-overlay">
                    <div style={{ fontSize: 42 }}>{previewData.icon}</div>
                    <h1>{previewData.title}</h1>
                    <p>{previewData.subtitle}</p>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="sp-preview-content">
                <p className="sp-preview-intro">{previewData.intro}</p>
                <p className="sp-preview-intro">{previewData.intro2}</p>

                {/* Process steps */}
                {previewData.processSteps && previewData.processSteps.length > 0 && (
                  <div className="sp-preview-section">
                    <h3>{previewData.processTitle}</h3>
                    <div className="sp-preview-steps">
                      {previewData.processSteps.map((s, i) => (
                        <div key={i} className="sp-preview-step">
                          <div className="sp-preview-step-num">{s.step}</div>
                          <div><strong>{s.title}</strong><p>{s.desc}</p></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                {previewData.features && previewData.features.length > 0 && (
                  <div className="sp-preview-section">
                    <h3>{previewData.featuresTitle}</h3>
                    <div className="sp-preview-features">
                      {previewData.features.map((f, i) => (
                        <div key={i} className="sp-preview-feature">
                          <span>{f.icon}</span><strong>{f.title}</strong><p>{f.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specs */}
                {previewData.specs && previewData.specs.length > 0 && (
                  <div className="sp-preview-section">
                    <h3>{previewData.specsTitle}</h3>
                    <table className="sp-preview-table">
                      <tbody>
                        {previewData.specs.map((row, i) => (
                          <tr key={i}><td>{row[0]}</td><td>{row[1]}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Images */}
                {previewData.images && previewData.images.length > 0 && (
                  <div className="sp-preview-section">
                    <h3>Service Images</h3>
                    <div className="sp-preview-img-grid">
                      {previewData.images.map((img, i) => (
                        <div key={i} className="sp-preview-img-card">
                          <img src={img.url} alt={img.caption || ''} />
                          {img.caption && <p>{img.caption}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="sp-preview-cta">
                  <h3>{previewData.ctaTitle}</h3>
                  <p>{previewData.ctaText}</p>
                  <div className="sp-preview-cta-btn">Get a Free Quote</div>
                </div>
              </div>
            </div>
            <div className="sp-preview-modal-footer">
              <button className="btn btn-secondary" onClick={() => setPreviewOpen(false)}>Close Preview</button>
              <button className="btn btn-publish" onClick={() => { setPreviewOpen(false); handlePublish(); }} disabled={publishing}>
                {publishing ? 'Publishing...' : '🚀 Publish Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service banner crop modal */}
      {bannerCropSrc && (
        <ImageCropModal
          imageSrc={bannerCropSrc}
          aspect={16 / 9}
          title="Crop Service Banner (16 : 9)"
          onCancel={handleBannerCropCancel}
          onConfirm={handleBannerCropConfirm}
          outputWidth={1920}
          outputHeight={1080}
        />
      )}

      {/* Service content image crop modal */}
      {imgCropQueue.length > 0 && (
        <ImageCropModal
          imageSrc={imgCropQueue[0].previewUrl}
          aspect={null}
          title={`Crop Image${imgCropQueue.length > 1 ? ` (${imgCropQueue.length} remaining)` : ''}`}
          onCancel={handleImgCropCancel}
          onConfirm={handleImgCropConfirm}
          outputWidth={1200}
          extraActions={
            <button className="crop-btn-skip" onClick={handleImgCropSkip}>Skip Crop</button>
          }
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Site Info Section (About Us + Contact Details) ───────────────
// ═══════════════════════════════════════════════════════════════════
const SITE_INFO_DEFAULTS = {
  aboutHeading: 'About Elite Borewells',
  aboutText: '',
  aboutPoints: [],
  phone: '',
  phone2: '',
  email: '',
  address: '',
  mapLink: '',
  mapEmbed: '',
  footerText: '',
  facebook: '',
  instagram: '',
  youtube: '',
  linkedin: '',
  twitter: '',
  // SEO Meta Tags
  seoTitle: 'Elite Borewells - Professional Borewell Drilling & Water Solutions',
  seoDescription: 'Elite Borewells provides expert borewell drilling, cleaning, pump services, soil testing, and water solutions. Trusted borewell contractors with 15+ years of experience. Get a free quote today!',
  seoKeywords: '',
  seoNegativeKeywords: '',
};

function SiteInfoSection() {
  const [info, setInfo] = useState(SITE_INFO_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {type:'success'|'error', text}

  useEffect(() => {
    fetch(`${API}/api/site-info`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setInfo(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${API}/api/site-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(info),
      });
      const d = await res.json();
      if (d.success) {
        setMsg({ type: 'success', text: 'Saved successfully!' });
      } else {
        setMsg({ type: 'error', text: d.message || 'Save failed.' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Network error. Try again.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const addPoint = () => setInfo((f) => ({ ...f, aboutPoints: [...(f.aboutPoints || []), ''] }));
  const removePoint = (i) => setInfo((f) => ({ ...f, aboutPoints: f.aboutPoints.filter((_, idx) => idx !== i) }));
  const updatePoint = (i, val) => setInfo((f) => {
    const pts = [...f.aboutPoints];
    pts[i] = val;
    return { ...f, aboutPoints: pts };
  });

  if (loading) return <div className="sp-loading"><div className="sp-spinner" /><p>Loading...</p></div>;

  return (
    <div className="si-wrap">

      {/* ── About Us Card ─────────────────────────────────── */}
      <div className="si-card">
        <div className="si-card-header">
          <div className="si-card-icon">🏢</div>
          <div>
            <div className="si-card-title">About Us</div>
            <div className="si-card-subtitle">Displayed in the About section on the homepage</div>
          </div>
        </div>
        <div className="si-card-body">
          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">✏️</span> Section Heading
            </label>
            <input
              className="si-input"
              value={info.aboutHeading || ''}
              onChange={(e) => setInfo((f) => ({ ...f, aboutHeading: e.target.value }))}
              placeholder="About Elite Borewells"
            />
          </div>

          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">📝</span> Company Description
            </label>
            <textarea
              className="si-textarea"
              rows={4}
              value={info.aboutText || ''}
              onChange={(e) => setInfo((f) => ({ ...f, aboutText: e.target.value }))}
              placeholder="Write a short description of your company..."
            />
            <div className="si-hint">{(info.aboutText || '').length} characters</div>
          </div>

          <div className="si-field">
            <div className="si-points-header">
              <div className="si-points-title">
                <span className="si-label-icon">✅</span>
                <span>Highlight Points</span>
                <span className="si-points-count">{(info.aboutPoints || []).length}</span>
              </div>
              <button type="button" className="si-add-btn" onClick={addPoint} disabled={(info.aboutPoints || []).length >= 8}>
                + Add Point
              </button>
            </div>
            <div className="si-points-list">
              {(info.aboutPoints || []).length === 0 && (
                <div className="si-points-empty">No highlight points yet. Click "+ Add Point" to add one.</div>
              )}
              {(info.aboutPoints || []).map((pt, i) => (
                <div key={i} className="si-point-row">
                  <div className="si-point-drag">☰</div>
                  <div className="si-point-num">{i + 1}</div>
                  <input
                    className="si-input si-point-input"
                    value={pt}
                    onChange={(e) => updatePoint(i, e.target.value)}
                    placeholder={`e.g. 15+ Years of Experience`}
                  />
                  <button type="button" className="si-remove-btn" onClick={() => removePoint(i)} title="Remove point">✕</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Contact Details Card ──────────────────────────── */}
      <div className="si-card">
        <div className="si-card-header">
          <div className="si-card-icon">📞</div>
          <div>
            <div className="si-card-title">Contact Details</div>
            <div className="si-card-subtitle">Shown in the Contact section and footer</div>
          </div>
        </div>
        <div className="si-card-body">
          <div className="si-contact-grid">
            <div className="si-field">
              <label className="si-label">
                <span className="si-label-icon">📞</span> Primary Phone
              </label>
              <input
                className="si-input"
                value={info.phone || ''}
                onChange={(e) => setInfo((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+91 98765 43210"
                type="tel"
              />
            </div>
            <div className="si-field">
              <label className="si-label">
                <span className="si-label-icon">📞</span> Additional Phone <span className="si-optional-tag">optional</span>
              </label>
              <input
                className="si-input"
                value={info.phone2 || ''}
                onChange={(e) => setInfo((f) => ({ ...f, phone2: e.target.value }))}
                placeholder="+91 98765 43211"
                type="tel"
              />
            </div>
            <div className="si-field">
              <label className="si-label">
                <span className="si-label-icon">📧</span> Email Address
              </label>
              <input
                className="si-input"
                value={info.email || ''}
                onChange={(e) => setInfo((f) => ({ ...f, email: e.target.value }))}
                placeholder="info@eliteborewells.com"
                type="email"
              />
            </div>
          </div>
          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">📍</span> Office Address
            </label>
            <input
              className="si-input"
              value={info.address || ''}
              onChange={(e) => setInfo((f) => ({ ...f, address: e.target.value }))}
              placeholder="123 Water Street, Bangalore, Karnataka"
            />
          </div>
          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">🔗</span> Google Maps Share Link
              {info.mapLink ? <span className="si-social-live">● Live</span> : <span className="si-social-hidden">Hidden</span>}
            </label>
            <input
              className="si-input"
              value={info.mapLink || ''}
              onChange={(e) => setInfo((f) => ({ ...f, mapLink: e.target.value }))}
              placeholder="https://maps.app.goo.gl/..."
              type="url"
            />
            <div className="si-hint">Open Google Maps → find your location → tap Share → Copy link. Paste here. This opens Google Maps when customer clicks "Open in Google Maps".</div>
          </div>
          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">🗺️</span> Google Maps Embed URL
              {info.mapEmbed ? <span className="si-social-live">● Live</span> : <span className="si-social-hidden">Hidden</span>}
            </label>
            <input
              className="si-input"
              value={info.mapEmbed || ''}
              onChange={(e) => setInfo((f) => ({ ...f, mapEmbed: e.target.value }))}
              placeholder="Paste the full <iframe>...</iframe> code or just the URL"
              type="text"
            />
            <div className="si-hint">Google Maps → Share → Embed a map → Copy HTML → paste the full code here. You can paste either the full &lt;iframe&gt; code or just the URL — both work.</div>
          </div>
        </div>
      </div>

      {/* ── Social Media Card ─────────────────────────────── */}
      <div className="si-card">
        <div className="si-card-header">
          <div className="si-card-icon">🌐</div>
          <div>
            <div className="si-card-title">Social Media Links</div>
            <div className="si-card-subtitle">Leave blank to hide — filled links appear in the footer</div>
          </div>
        </div>
        <div className="si-card-body">
          <div className="si-social-grid">
            {[
              { key: 'facebook',  icon: '📘', label: 'Facebook',  placeholder: 'https://facebook.com/yourpage' },
              { key: 'instagram', icon: '📸', label: 'Instagram', placeholder: 'https://instagram.com/yourhandle' },
              { key: 'youtube',   icon: '▶️',  label: 'YouTube',   placeholder: 'https://youtube.com/@yourchannel' },
              { key: 'linkedin',  icon: '💼', label: 'LinkedIn',  placeholder: 'https://linkedin.com/company/yourco' },
              { key: 'twitter',   icon: '🐦', label: 'X / Twitter', placeholder: 'https://x.com/yourhandle' },
            ].map(({ key, icon, label, placeholder }) => (
              <div className="si-field" key={key}>
                <label className="si-label">
                  <span className="si-label-icon">{icon}</span> {label}
                  {info[key] ? <span className="si-social-live">● Live</span> : <span className="si-social-hidden">Hidden</span>}
                </label>
                <input
                  className="si-input"
                  value={info[key] || ''}
                  onChange={(e) => setInfo((f) => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  type="url"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SEO Meta Tags Card ────────────────────────────── */}
      <div className="si-card">
        <div className="si-card-header">
          <div className="si-card-icon">🔍</div>
          <div>
            <div className="si-card-title">SEO Meta Tags</div>
            <div className="si-card-subtitle">Optimize your website for search engines like Google</div>
          </div>
        </div>
        <div className="si-card-body">
          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">📑</span> SEO Title
            </label>
            <input
              className="si-input"
              value={info.seoTitle || ''}
              onChange={(e) => setInfo((f) => ({ ...f, seoTitle: e.target.value }))}
              placeholder="Elite Borewells - Professional Borewell Drilling & Water Solutions"
              maxLength={60}
            />
            <div className="si-hint">{(info.seoTitle || '').length}/60 characters — Appears in Google search results as the page title.</div>
          </div>

          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">📝</span> SEO Description
            </label>
            <textarea
              className="si-textarea"
              rows={3}
              value={info.seoDescription || ''}
              onChange={(e) => setInfo((f) => ({ ...f, seoDescription: e.target.value }))}
              placeholder="Elite Borewells provides expert borewell drilling, cleaning, pump services, soil testing, and water solutions. Trusted borewell contractors with 15+ years of experience."
              maxLength={160}
            />
            <div className="si-hint">{(info.seoDescription || '').length}/160 characters — Appears below the title in Google search results.</div>
          </div>

          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">🏷️</span> SEO Keywords <span className="si-optional-tag">optional</span>
            </label>
            <input
              className="si-input"
              value={info.seoKeywords || ''}
              onChange={(e) => setInfo((f) => ({ ...f, seoKeywords: e.target.value }))}
              placeholder="borewell drilling, borewell cleaning, pump sales service, soil test, borewell contractors, water solutions"
            />
            <div className="si-hint">Comma-separated keywords that describe your business and services.</div>
          </div>

          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">🚫</span> Negative Keywords <span className="si-optional-tag">optional</span>
            </label>
            <input
              className="si-input"
              value={info.seoNegativeKeywords || ''}
              onChange={(e) => setInfo((f) => ({ ...f, seoNegativeKeywords: e.target.value }))}
              placeholder="free, cheap, DIY, discount"
            />
            <div className="si-hint">Comma-separated keywords to avoid attracting unqualified traffic (e.g., 'free', 'cheap'). This won't affect Google indexing but helps you track what NOT to target.</div>
          </div>
        </div>
      </div>

      {/* ── Footer Card ────────────────────────────────────── */}
      <div className="si-card">
        <div className="si-card-header">
          <div className="si-card-icon">📄</div>
          <div>
            <div className="si-card-title">Footer</div>
            <div className="si-card-subtitle">Copyright text displayed at the bottom of every page</div>
          </div>
        </div>
        <div className="si-card-body">
          <div className="si-field">
            <label className="si-label">
              <span className="si-label-icon">©</span> Footer Text
            </label>
            <input
              className="si-input"
              value={info.footerText || ''}
              onChange={(e) => setInfo((f) => ({ ...f, footerText: e.target.value }))}
              placeholder="© 2025 Elite Borewells. All rights reserved."
            />
            <div className="si-hint">Tip: Include the current year and your company name.</div>
          </div>
        </div>
      </div>

      {/* ── Save Bar ───────────────────────────────────────── */}
      <div className="si-save-bar">
        {msg && (
          <div className={`si-msg ${msg.type === 'success' ? 'si-msg-success' : 'si-msg-error'}`}>
            {msg.type === 'success' ? '✅' : '❌'} {msg.text}
          </div>
        )}
        <button className="si-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? (
            <><span className="si-save-spinner" /> Saving...</>
          ) : (
            '💾  Save All Changes'
          )}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Enquiries Section ────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function EnquiriesSection({ onUnreadChange }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [folder, setFolder] = useState('unread'); // 'unread' | 'read' | 'contacted' | 'completed'
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // { ids: [], bulk: bool }
  const [viewModal, setViewModal] = useState(null); // enquiry object to view in modal

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  // Fetch all enquiries on mount
  const fetchEnquiries = useCallback(() => {
    fetch(`${API}/api/enquiries`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setEnquiries(d.enquiries);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);

  // Update unread count whenever enquiries change
  useEffect(() => {
    if (onUnreadChange) {
      const unreadCount = enquiries.filter((e) => !e.read && !e.contacted && !e.isJunk).length;
      onUnreadChange(unreadCount);
    }
  }, [enquiries, onUnreadChange]);

  // Clear selection when folder changes
  useEffect(() => { setSelected(new Set()); setExpanded(null); }, [folder]);

  // WebSocket for real-time notifications
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_WS_URL;
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'NEW_ENQUIRY') {
            setEnquiries((prev) => {
              // Prevent duplicates - check if this enquiry already exists
              const exists = prev.some((enq) => enq.id === data.enquiry.id);
              if (exists) return prev;
              return [data.enquiry, ...prev];
            });
            playNotificationSound();
          }
        } catch {}
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [onUnreadChange]);

  const playNotificationSound = () => {
    playNotifSound();
  };

  const markRead = async (id) => {
    await fetch(`${API}/api/enquiries/${id}/read`, { method: 'PATCH' }).catch(() => {});
    const now = new Date().toISOString();
    setEnquiries((prev) => prev.map((e) => e.id === id ? { ...e, read: true, readAt: e.readAt || now } : e));
  };

  const markContacted = async (id) => {
    await fetch(`${API}/api/enquiries/${id}/contacted`, { method: 'PATCH' }).catch(() => {});
    const now = new Date().toISOString();
    setEnquiries((prev) => prev.map((e) => e.id === id ? { ...e, read: true, readAt: e.readAt || now, contacted: true, contactedAt: e.contactedAt || now } : e));
  };

  // Move single enquiry to Completed (junk)
  const moveToCompleted = async (id) => {
    const enq = enquiries.find((e) => e.id === id);
    let fromState = 'unread';
    if (enq) {
      if (enq.contacted) fromState = 'contacted';
      else if (enq.read) fromState = 'read';
    }
    await fetch(`${API}/api/enquiries/${id}/junk`, { method: 'PATCH' }).catch(() => {});
    const now = new Date().toISOString();
    setEnquiries((prev) => prev.map((e) => e.id === id ? { ...e, isJunk: true, junkAt: now, junkFromState: e.junkFromState || fromState } : e));
    if (expanded === id) setExpanded(null);
    setSelected((prev) => { const s = new Set(prev); s.delete(id); return s; });
  };

  // Bulk move to Completed
  const bulkMoveToCompleted = async (ids) => {
    await fetch(`${API}/api/enquiries/junk/bulk`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    }).catch(() => {});
    const now = new Date().toISOString();
    setEnquiries((prev) => prev.map((e) => {
      if (!ids.includes(e.id)) return e;
      let fromState = 'unread';
      if (e.contacted) fromState = 'contacted';
      else if (e.read) fromState = 'read';
      return { ...e, isJunk: true, junkAt: now, junkFromState: e.junkFromState || fromState };
    }));
    setSelected(new Set());
    if (ids.includes(expanded)) setExpanded(null);
  };

  // Permanent delete (only from Completed folder) — two-step via confirmDelete state
  const permanentDelete = async (ids) => {
    if (ids.length === 1) {
      await fetch(`${API}/api/enquiries/${ids[0]}`, { method: 'DELETE' }).catch(() => {});
    } else {
      await fetch(`${API}/api/enquiries/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      }).catch(() => {});
    }
    setEnquiries((prev) => {
      const updated = prev.filter((e) => !ids.includes(e.id));
      onUnreadChange && onUnreadChange(updated.filter((e) => !e.read && !e.contacted && !e.isJunk).length);
      return updated;
    });
    setSelected(new Set());
    if (ids.includes(expanded)) setExpanded(null);
    setConfirmDelete(null);
  };

  const toggleExpand = (id) => {
    const enq = enquiries.find((e) => e.id === id);
    // If unread, show in modal instead of expanding inline
    if (enq && !enq.read && !enq.contacted && !enq.isJunk) {
      setViewModal(enq);
    } else {
      // For read/contacted/completed, just toggle expand
      setExpanded((prev) => (prev === id ? null : id));
    }
  };

  const handleMarkAsReadFromModal = async () => {
    if (viewModal) {
      await markRead(viewModal.id);
      setViewModal(null);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  // Filter enquiries by folder
  const folderEnquiries = enquiries.filter((e) => {
    if (folder === 'unread') return !e.read && !e.contacted && !e.isJunk;
    if (folder === 'read') return e.read && !e.contacted && !e.isJunk;
    if (folder === 'contacted') return e.contacted && !e.isJunk;
    if (folder === 'completed') return e.isJunk === true;
    return true;
  });

  // Apply search + date filters
  const filteredEnquiries = folderEnquiries.filter((e) => {
    const q = search.trim().toLowerCase();
    if (q) {
      const matchName = e.name.toLowerCase().includes(q);
      const matchPhone = e.phone.toLowerCase().includes(q);
      if (!matchName && !matchPhone) return false;
    }
    if (dateFrom) {
      if (new Date(e.submittedAt) < new Date(dateFrom)) return false;
    }
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      if (new Date(e.submittedAt) > end) return false;
    }
    return true;
  });

  const allSelected = filteredEnquiries.length > 0 && filteredEnquiries.every((e) => selected.has(e.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected((prev) => {
        const s = new Set(prev);
        filteredEnquiries.forEach((e) => s.delete(e.id));
        return s;
      });
    } else {
      setSelected((prev) => {
        const s = new Set(prev);
        filteredEnquiries.forEach((e) => s.add(e.id));
        return s;
      });
    }
  };

  const selectedInView = filteredEnquiries.filter((e) => selected.has(e.id)).map((e) => e.id);

  const unreadCount = enquiries.filter((e) => !e.read && !e.contacted && !e.isJunk).length;
  const readCount = enquiries.filter((e) => e.read && !e.contacted && !e.isJunk).length;
  const contactedCount = enquiries.filter((e) => e.contacted && !e.isJunk).length;
  const completedCount = enquiries.filter((e) => e.isJunk).length;

  const FOLDER_TABS = [
    { key: 'unread', label: 'Unread', count: unreadCount, icon: '📬' },
    { key: 'read', label: 'Read', count: readCount, icon: '📖' },
    { key: 'contacted', label: 'Contacted', count: contactedCount, icon: '✅' },
    { key: 'completed', label: 'Completed', count: completedCount, icon: '🗂️' },
  ];

  const junkFromStateLabel = (state) => {
    if (state === 'unread') return 'Unread';
    if (state === 'read') return 'Read';
    if (state === 'contacted') return 'Contacted';
    return state || '—';
  };

  if (loading) return <div className="sp-loading"><div className="sp-spinner" /><p>Loading enquiries...</p></div>;

  return (
    <div className="enq-wrap">
      {/* Stats row */}
      <div className="enq-stats">
        <div className="enq-stat-card enq-stat-unread">
          <span className="enq-stat-num">{unreadCount}</span>
          <span className="enq-stat-label">Unread</span>
        </div>
        <div className="enq-stat-card">
          <span className="enq-stat-num">{readCount}</span>
          <span className="enq-stat-label">Read</span>
        </div>
        <div className="enq-stat-card enq-stat-contacted">
          <span className="enq-stat-num">{contactedCount}</span>
          <span className="enq-stat-label">Contacted</span>
        </div>
        <div className="enq-stat-card enq-stat-completed">
          <span className="enq-stat-num">{completedCount}</span>
          <span className="enq-stat-label">Completed</span>
        </div>
      </div>

      {/* Folder tabs */}
      <div className="enq-folder-tabs">
        {FOLDER_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`enq-folder-tab${folder === tab.key ? ' enq-folder-tab-active' : ''}`}
            onClick={() => setFolder(tab.key)}
          >
            <span className="enq-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.count > 0 && <span className="enq-tab-badge">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Search & date filters */}
      <div className="enq-filters">
        <input
          className="enq-search"
          type="text"
          placeholder="Search by name or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="enq-date-filters">
          <label className="enq-date-label">From</label>
          <input className="enq-date-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <label className="enq-date-label">To</label>
          <input className="enq-date-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          {(search || dateFrom || dateTo) && (
            <button className="enq-clear-filters" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}>Clear</button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {filteredEnquiries.length > 0 && (
        <div className="enq-bulk-bar">
          <label className="enq-select-all">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            <span>{allSelected ? 'Deselect All' : 'Select All'} ({filteredEnquiries.length})</span>
          </label>
          {selectedInView.length > 0 && (
            <div className="enq-bulk-actions">
              <span className="enq-selected-count">{selectedInView.length} selected</span>
              {folder !== 'completed' && (
                <button className="enq-btn enq-btn-move-junk" onClick={() => bulkMoveToCompleted(selectedInView)}>
                  Move to Completed
                </button>
              )}
              {folder === 'completed' && (
                <button
                  className="enq-btn enq-btn-delete"
                  onClick={() => setConfirmDelete({ ids: selectedInView, bulk: true })}
                >
                  Delete Permanently
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {filteredEnquiries.length === 0 && (
        <div className="enq-empty">
          <div className="enq-empty-icon">{folder === 'completed' ? '🗂️' : '📭'}</div>
          <p>
            {search || dateFrom || dateTo
              ? 'No enquiries match your search/filter.'
              : folder === 'completed'
                ? 'No completed enquiries.'
                : `No ${folder} enquiries.`}
          </p>
        </div>
      )}

      {/* Enquiry list */}
      <div className="enq-list">
        {filteredEnquiries.map((enq) => {
          const isCompleted = enq.isJunk;
          return (
            <div
              key={enq.id}
              className={`enq-card${isCompleted ? ' enq-card-completed' : enq.contacted ? ' enq-card-contacted' : !enq.read ? ' enq-card-unread' : ''}${expanded === enq.id ? ' enq-card-open' : ''}${selected.has(enq.id) ? ' enq-card-selected' : ''}`}
            >
              <div className="enq-card-header">
                <label className="enq-card-checkbox" onClick={(ev) => ev.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(enq.id)}
                    onChange={() => toggleSelect(enq.id)}
                  />
                </label>
                <div className="enq-card-header-inner" onClick={() => toggleExpand(enq.id)}>
                  <div className="enq-card-left">
                    {!enq.read && !enq.contacted && !enq.isJunk && <span className="enq-dot" />}
                    <div className="enq-name">{enq.name}</div>
                    <div className="enq-phone">📞 {enq.phone}</div>
                  </div>
                  <div className="enq-card-right">
                    {isCompleted
                      ? <span className="enq-status-pill enq-status-completed">Completed</span>
                      : enq.contacted
                        ? <span className="enq-status-pill enq-status-contacted">Contacted</span>
                        : enq.read
                          ? <span className="enq-status-pill enq-status-read">Read</span>
                          : <span className="enq-status-pill enq-status-unread">Unread</span>
                    }
                    <div className="enq-time">{fmtDate(enq.submittedAt)}</div>
                    <span className="enq-expand-icon">{expanded === enq.id ? '▲' : '▼'}</span>
                  </div>
                </div>
              </div>

              {expanded === enq.id && (
                <div className="enq-card-body">
                  {enq.email && (
                    <div className="enq-detail-row">
                      <span className="enq-detail-label">Email</span>
                      <span>{enq.email}</span>
                    </div>
                  )}
                  <div className="enq-detail-row">
                    <span className="enq-detail-label">Message</span>
                    <span className="enq-message">{enq.message}</span>
                  </div>

                  {/* Timestamps */}
                  <div className="enq-timestamps">
                    <div className="enq-ts-row">
                      <span className="enq-ts-label">Created</span>
                      <span className="enq-ts-val">{fmtDate(enq.submittedAt)}</span>
                    </div>
                    {enq.readAt && (
                      <div className="enq-ts-row">
                        <span className="enq-ts-label">Read</span>
                        <span className="enq-ts-val">{fmtDate(enq.readAt)}</span>
                      </div>
                    )}
                    {enq.contactedAt && (
                      <div className="enq-ts-row">
                        <span className="enq-ts-label">Contacted</span>
                        <span className="enq-ts-val">{fmtDate(enq.contactedAt)}</span>
                      </div>
                    )}
                    {enq.junkAt && (
                      <div className="enq-ts-row">
                        <span className="enq-ts-label">Moved to Completed</span>
                        <span className="enq-ts-val">
                          {fmtDate(enq.junkAt)}
                          {enq.junkFromState && (
                            <span className="enq-junk-from"> (was {junkFromStateLabel(enq.junkFromState)})</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="enq-actions">
                    {!isCompleted && !enq.read && !enq.contacted && (
                      <button className="enq-btn enq-btn-read" onClick={() => markRead(enq.id)}>
                        Mark as Read
                      </button>
                    )}
                    {!isCompleted && enq.read && !enq.contacted && (
                      <button className="enq-btn enq-btn-contacted" onClick={() => markContacted(enq.id)}>
                        Contacted
                      </button>
                    )}
                    {!isCompleted && (
                      <button className="enq-btn enq-btn-move-junk" onClick={() => moveToCompleted(enq.id)}>
                        Move to Completed
                      </button>
                    )}
                    {isCompleted && (
                      <button
                        className="enq-btn enq-btn-delete"
                        onClick={() => setConfirmDelete({ ids: [enq.id], bulk: false })}
                      >
                        Delete Permanently
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* View Enquiry Modal (for unread messages) */}
      {viewModal && (
        <div className="enq-modal-overlay" onClick={() => setViewModal(null)}>
          <div className="enq-modal enq-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enq-modal-header">
              <div className="enq-modal-icon">📧</div>
              <h3 className="enq-modal-title">New Enquiry</h3>
              <button className="enq-modal-close" onClick={() => setViewModal(null)}>✕</button>
            </div>

            <div className="enq-modal-content">
              <div className="enq-modal-field">
                <span className="enq-modal-label">Name</span>
                <span className="enq-modal-value">{viewModal.name}</span>
              </div>

              <div className="enq-modal-field">
                <span className="enq-modal-label">Phone</span>
                <span className="enq-modal-value">📞 {viewModal.phone}</span>
              </div>

              {viewModal.email && (
                <div className="enq-modal-field">
                  <span className="enq-modal-label">Email</span>
                  <span className="enq-modal-value">📧 {viewModal.email}</span>
                </div>
              )}

              <div className="enq-modal-field">
                <span className="enq-modal-label">Message</span>
                <span className="enq-modal-value enq-modal-message">{viewModal.message}</span>
              </div>

              <div className="enq-modal-field">
                <span className="enq-modal-label">Submitted</span>
                <span className="enq-modal-value">{fmtDate(viewModal.submittedAt)}</span>
              </div>
            </div>

            <div className="enq-modal-actions">
              <button className="enq-btn enq-btn-cancel" onClick={() => setViewModal(null)}>
                Close
              </button>
              <button className="enq-btn enq-btn-read" onClick={handleMarkAsReadFromModal}>
                Mark as Read
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Two-step permanent delete confirmation modal */}
      {confirmDelete && (
        <div className="enq-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="enq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enq-modal-icon">⚠️</div>
            <h3 className="enq-modal-title">Permanently Delete?</h3>
            <p className="enq-modal-body">
              {confirmDelete.ids.length === 1
                ? 'This enquiry will be permanently removed and cannot be recovered.'
                : `${confirmDelete.ids.length} enquiries will be permanently removed and cannot be recovered.`}
            </p>
            <div className="enq-modal-actions">
              <button className="enq-btn enq-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="enq-btn enq-btn-delete-confirm" onClick={() => permanentDelete(confirmDelete.ids)}>
                Yes, Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── Root Admin App ───────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
// Preload notification sound once
const notifAudio = new Audio('/notification.wav');
notifAudio.volume = 1.0;

function playNotifSound() {
  try {
    notifAudio.currentTime = 0;
    notifAudio.play().catch(() => {});
  } catch {}
}

function App() {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  // Initialize activePage from URL hash, fallback to 'homepage'
  const getInitialPage = () => {
    const hash = window.location.hash.substring(1); // Remove the # character
    const validPages = ['homepage', 'services', 'gallery', 'siteinfo', 'enquiries'];
    return validPages.includes(hash) ? hash : 'homepage';
  };
  const [activePage, setActivePage] = useState(getInitialPage());
  const [unreadEnquiries, setUnreadEnquiries] = useState(0);
  const [toast, setToast] = useState(null); // { name, phone, message }
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toastTimerRef = useRef(null);

  // Sync URL hash with activePage state
  useEffect(() => {
    // Update URL hash when activePage changes
    window.location.hash = activePage;
  }, [activePage]);

  // Listen for hash changes (browser back/forward buttons)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      const validPages = ['homepage', 'services', 'gallery', 'siteinfo', 'enquiries'];
      if (validPages.includes(hash)) {
        setActivePage(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Check password version periodically (every 30 seconds)
  useEffect(() => {
    if (!loggedIn) return;

    const interval = setInterval(async () => {
      const stillValid = await checkPasswordVersion();
      if (!stillValid) {
        // Password changed elsewhere, logout
        setLoggedIn(false);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [loggedIn]);

  // WebSocket at App level for badge updates on ALL pages
  useEffect(() => {
    // Load initial unread count
    fetch(`${API}/api/enquiries`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setUnreadEnquiries(d.enquiries.filter((e) => !e.read).length);
      })
      .catch(() => {});

    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(process.env.REACT_APP_WS_URL);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'NEW_ENQUIRY') {
            setUnreadEnquiries((n) => n + 1);
            // Show toast popup
            setToast({ name: data.enquiry.name, phone: data.enquiry.phone, message: data.enquiry.message });
            clearTimeout(toastTimerRef.current);
            toastTimerRef.current = setTimeout(() => setToast(null), 6000);
            // Play sound
            playNotifSound();
          }
        } catch {}
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => { clearTimeout(reconnectTimer); if (ws) ws.close(); };
  }, []);

  // Show login page if not authenticated (all hooks run above, safe here)
  if (!loggedIn) {
    return <LoginPage onLogin={() => setLoggedIn(true)} />;
  }

  return (
    <div className="admin-root">
      {/* Mobile overlay — tap to close sidebar */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
        <div className="sidebar-brand">
          <span className="brand-icon">⚙</span>
          <span className="brand-text">Admin Panel</span>
          {/* Close button visible only on mobile */}
          <button className="sidebar-close-btn" onClick={() => setSidebarOpen(false)} aria-label="Close menu">✕</button>
        </div>
        <nav className="sidebar-nav">
          <div className={`nav-item ${activePage === 'homepage' ? 'active' : ''}`} onClick={() => { setActivePage('homepage'); setSidebarOpen(false); }}>
            <span className="nav-icon">🏠</span> Home Page
          </div>
          <div className={`nav-item ${activePage === 'services' ? 'active' : ''}`} onClick={() => { setActivePage('services'); setSidebarOpen(false); }}>
            <span className="nav-icon">📄</span> Service Pages
          </div>
          <div className={`nav-item ${activePage === 'gallery' ? 'active' : ''}`} onClick={() => { setActivePage('gallery'); setSidebarOpen(false); }}>
            <span className="nav-icon">🖼️</span> Gallery Management
          </div>
          <div className={`nav-item ${activePage === 'siteinfo' ? 'active' : ''}`} onClick={() => { setActivePage('siteinfo'); setSidebarOpen(false); }}>
            <span className="nav-icon">🏢</span> About &amp; Contact
          </div>
          <div className={`nav-item ${activePage === 'enquiries' ? 'active' : ''}`} onClick={() => { setActivePage('enquiries'); setSidebarOpen(false); }}>
            <span className="nav-icon">📩</span> Enquiries
            {unreadEnquiries > 0 && (
              <span className="enq-nav-badge">{unreadEnquiries}</span>
            )}
          </div>
        </nav>
        <button
          className="sidebar-logout-btn"
          onClick={() => { doLogout(); setLoggedIn(false); }}
        >
          🔒 Logout
        </button>
      </aside>

      <main className="main-content">
        {/* Mobile top bar with hamburger */}
        <div className="mobile-topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <span /><span /><span />
          </button>
          <span className="mobile-topbar-title">
            {activePage === 'homepage' && 'Home Page'}
            {activePage === 'services' && 'Service Pages'}
            {activePage === 'gallery' && 'Gallery'}
            {activePage === 'siteinfo' && 'About & Contact'}
            {activePage === 'enquiries' && <>Enquiries {unreadEnquiries > 0 && <span className="enq-nav-badge">{unreadEnquiries}</span>}</>}
          </span>
        </div>
        {activePage === 'homepage' && (
          <>
            <div className="page-header">
              <h1>Home Page</h1>
              <p>Manage the logo, banner, and service cards displayed on the customer website homepage.</p>
            </div>
            <div className="homepage-section-block">
              <div className="homepage-section-title">
                <span className="homepage-section-icon">🖼</span> Logo Management
              </div>
              <p className="homepage-section-desc">Upload and manage the logo displayed on the customer website.</p>
              <LogoSection />
            </div>
            <div className="homepage-section-divider" />
            <div className="homepage-section-block">
              <div className="homepage-section-title">
                <span className="homepage-section-icon">🏞️</span> Banner Management
              </div>
              <p className="homepage-section-desc">Upload the hero banner image displayed on the homepage. Replaces the default gradient background.</p>
              <BannerSection />
            </div>
            <div className="homepage-section-divider" />
            <div className="homepage-section-block">
              <div className="homepage-section-title">
                <span className="homepage-section-icon">🗂️</span> Service Cards
              </div>
              <p className="homepage-section-desc">
                Edit the title, description, and image for each of the 8 service cards shown on the homepage.
                Upload an image to replace the default emoji icon on that card.
              </p>
              <HomeServicesSection />
            </div>
          </>
        )}
        {activePage === 'services' && (
          <>
            <div className="page-header">
              <h1>Service Pages</h1>
              <p>Edit banner, content, and images for each service page. Save as draft and preview before publishing live.</p>
            </div>
            <ServicePagesSection />
          </>
        )}
        {activePage === 'gallery' && (
          <>
            <div className="page-header">
              <h1>Gallery Management</h1>
              <p>Upload multiple photos and videos at once, set captions and thumbnails.</p>
            </div>
            <GallerySection />
          </>
        )}
        {activePage === 'siteinfo' && (
          <>
            <div className="page-header">
              <h1>About &amp; Contact</h1>
              <p>Edit the About Us section, contact details, and footer text shown on the customer website.</p>
            </div>
            <SiteInfoSection />
          </>
        )}
        {activePage === 'enquiries' && (
          <>
            <div className="page-header">
              <h1>Enquiries</h1>
              <p>Customer enquiries submitted via the contact form. New enquiries arrive in real time. Deleted enquiries move to Completed and are permanently removed from there.</p>
            </div>
            <EnquiriesSection onUnreadChange={setUnreadEnquiries} />
          </>
        )}
      </main>

      {/* New Enquiry Toast Notification */}
      {toast && (
        <div className="enq-toast" onClick={() => { setToast(null); setActivePage('enquiries'); }}>
          <div className="enq-toast-icon">📩</div>
          <div className="enq-toast-body">
            <div className="enq-toast-title">New Enquiry Received!</div>
            <div className="enq-toast-name">{toast.name} · {toast.phone}</div>
            <div className="enq-toast-msg">{toast.message}</div>
          </div>
          <button className="enq-toast-close" onClick={(e) => { e.stopPropagation(); setToast(null); }}>✕</button>
        </div>
      )}
    </div>
  );
}

export default App;
